import { Application, ColorMatrixFilter, Container, Graphics, MeshRope, NoiseFilter, Point, Rectangle, Sprite, Texture, Text } from 'pixi.js';
import { AdvancedBloomFilter } from '@pixi/filter-advanced-bloom';
import { GlowFilter } from '@pixi/filter-glow';
import { G, bindInput, keyLabel } from './state';
import { Player, pushMouseHistory } from './player';
import { ParticleSystem } from './particles';
import { BulletPool } from './bullets';
import { fire, WEAPONS } from './weapons';
import { EnemyManager, type Enemy } from './enemies';
import { EBulletPool, enemyFire } from './ebullets';
import { PowerupSystem, rollPowerup } from './powerups';
import { Starfield } from './stars';
import { buildWave, tickWave, wave } from './waves';
import { HUD } from './hud';
import { SFX, tickBGM, toggleMusic, setEngineState, isMusicOn, setSuspended } from './audio';
import { settings } from './settings';
import { fireAch, tickAchievements } from './achievements';
import { Terrain } from './terrain';
import { HazardSystem } from './hazards';
import { applyUpgradesToPlayer, dmgMultFromUpgrades, magnetRadiusMult, phoenixLevel, calcShardsEarned, addShards } from './prestige';
import { isTutorialDone, markTutorialDone, TUT_STEPS } from './tutorial';
import { dailyKey, setDailyRNG, setMathRNG } from './rng';
import { SPRITES } from './sprites';
import { getTrailTextures } from './trails';
import { keys, coop, isTouchDevice, MOBILE_ZOOM } from './state';
import { initTouchControls, showMobileInstructions, tickMobileInstructions } from './modules/touchControls';
import { Player2 } from './player2';
import { FloatTextSystem } from './floattext';
import { Screens } from './screens';
import { getShards } from './prestige';
import { MusicToggleButton } from './modules/musicToggleButton';
import { TitleNebula } from './modules/titleNebula';
import { drawNovaRing } from './modules/novaRing';
import { WipeOverlay } from './modules/wipeOverlay';
import { FlashOverlay } from './modules/flashOverlay';
import { Shockwaves } from './modules/shockwaves';
import { drawSlowFieldEdges } from './modules/slowFieldEdges';
import { drawSynergyAura, drawShieldSurge, drawChargeRing, drawStatusRings } from './modules/playerAuras';
import { Camera } from './modules/camera';
import { drawTitleFocusRing } from './modules/titleFocusRing';
import { drawDarknessMask } from './modules/darknessMask';
import { applyBiomeGrade } from './modules/biomeGrade';
import { tickBossPhaseShifts } from './modules/bossPhaseShifts';
import { AchievementBanner } from './modules/achievementBanner';
import { LowHpVignette } from './modules/lowHpVignette';
import { runWaveClearCeremony } from './modules/waveClearFX';
import { setHeroTitle as applyHeroTitle, setHeroSub as applyHeroSub } from './modules/heroOverlay';
import { drawBossTelegraph } from './modules/bossTelegraph';
import { syncMirrorBoss } from './modules/mirrorBossSync';
import { ScoreZones } from './modules/scoreZones';
import { WeaponAddons } from './modules/weaponAddons';
import { EngineAudio } from './modules/engineAudio';

const LB_KEYS = {
  endless: 'ns_lb_pixi_endless',
  campaign: 'ns_lb_pixi_campaign',
};

interface LBRow { score: number; date: string; }
function lbKey(): string {
  if (G.daily) return `ns_lb_daily_${dailyKey()}`;
  return G.endless ? LB_KEYS.endless : LB_KEYS.campaign;
}
function getLB(): LBRow[] {
  try { return JSON.parse(localStorage.getItem(lbKey()) || '[]'); }
  catch { return []; }
}
function getLBByKey(key: string): LBRow[] {
  try { return JSON.parse(localStorage.getItem(key) || '[]'); }
  catch { return []; }
}
function qualifiesForLeaderboard(score: number): boolean {
  if (score <= 0) return false;
  const lb = getLB();
  if (lb.length < 10) return true;
  return score > lb[9].score;
}
function addToLB(score: number): { lb: LBRow[]; idx: number } {
  const lb = getLB();
  lb.push({ score, date: new Date().toLocaleDateString() });
  lb.sort((a, b) => b.score - a.score);
  lb.splice(10);
  localStorage.setItem(lbKey(), JSON.stringify(lb));
  const idx = lb.findIndex((r) => r.score === score);
  return { lb, idx };
}

async function boot() {
  const app = new Application();
  await app.init({
    background: '#000010',
    resizeTo: window,
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
    antialias: true,
  });

  const mount = document.getElementById('app');
  if (!mount) throw new Error('#app mount point missing');
  mount.appendChild(app.canvas);

  bindInput();
  initTouchControls();

  const bg = new Container();
  bg.scale.set(MOBILE_ZOOM);
  app.stage.addChild(bg);
  const stars = new Starfield(bg);

  const world = new Container();
  world.scale.set(MOBILE_ZOOM);
  G.W = window.innerWidth / MOBILE_ZOOM;
  G.H = window.innerHeight / MOBILE_ZOOM;
  app.stage.addChild(world);

  // Neon bloom on the world (everything in playspace gets bloom)
  // ColorMatrixFilter for biome grading (saturation + hue per biome tier)
  // NoiseFilter for subtle film grain
  const colorMatrix = new ColorMatrixFilter();
  const filmGrain = new NoiseFilter({ noise: 0.04, seed: Math.random() });
  try {
    const bloom = new AdvancedBloomFilter({
      threshold: 0.25,
      bloomScale: 1.8,
      brightness: 1.05,
      blur: 6,
      quality: 6,
    });
    world.filters = [bloom as any, colorMatrix as any, filmGrain as any];
  } catch (e) {
    console.warn('[NOVA STRIKE] Bloom filter unavailable', e);
    world.filters = [colorMatrix as any, filmGrain as any];
  }

  const terrain = new Terrain(world);
  const hazards = new HazardSystem(world);
  const particles = new ParticleSystem(world);
  const bullets = new BulletPool(world);
  const enemies = new EnemyManager(world);
  const ebullets = new EBulletPool(world);
  const powerups = new PowerupSystem(world);
  const player = new Player(world);
  G.player = player;

  if (isTouchDevice) coop.enabled = false;
  let player2: Player2 | null = null;
  if (coop.enabled) player2 = new Player2(world, G.W * 0.4, G.H / 2);

  const floats = new FloatTextSystem(world);
  const hud = new HUD(app.stage);

  // DARKNESS arena overlay (radial dark mask around player)
  const darkness = new Graphics();
  app.stage.addChild(darkness);

  // Boss telegraph dashed aim-line
  const telegraph = new Graphics();
  app.stage.addChild(telegraph);

  // Charge ring around player
  const chargeRing = new Graphics();
  app.stage.addChild(chargeRing);

  // Nova-bomb expanding telegraph ring — populated by fireSpecial(0)
  const novaRing = new Graphics();
  novaRing.blendMode = 'add';
  app.stage.addChild(novaRing);
  let novaRingX = 0, novaRingY = 0, novaRingR = 0, novaRingT = 0;

  // Mission-comms beacon — pings every 10s during gameplay
  const engineAudio = new EngineAudio();

  // Shield-surge aura ring (visible only while fx.shieldSurge > 0)
  const surgeRing = new Graphics();
  surgeRing.blendMode = 'add';
  app.stage.addChild(surgeRing);

  // Slow-field full-screen tint overlay
  const slowOverlay = new Graphics();
  slowOverlay.blendMode = 'add';
  slowOverlay.visible = false;
  app.stage.addChild(slowOverlay);

  // Black-hole proximity warp — chromatic aberration + vignette around the player
  const bhWarpOverlay = new Graphics();
  bhWarpOverlay.blendMode = 'add';
  app.stage.addChild(bhWarpOverlay);

  // Synergy aura — pulsing magenta ring during fx.synergy > 0
  const synergyRing = new Graphics();
  synergyRing.blendMode = 'add';
  app.stage.addChild(synergyRing);

  // Player shield bubble — ring around player while player.shield > 0
  const playerShieldRing = new Graphics();
  playerShieldRing.blendMode = 'add';
  app.stage.addChild(playerShieldRing);

  // Low-HP red vignette — radial gradient texture, alpha driven by hp%
  const vignette = new LowHpVignette();
  app.stage.addChild(vignette.view);

  // Screen-flash overlay for bomb / boss-kill / phase-shift impact
  const flashOverlay = new FlashOverlay();
  app.stage.addChild(flashOverlay.view);
  const triggerFlash = (alpha: number, color = 0xffffff): void => flashOverlay.trigger(alpha, color);

  // Shockwave manager — fires Pixi ShockwaveFilters on bomb / boss-kill / phase-shift impacts
  const shockwaves = new Shockwaves(app.stage);
  const triggerShockwave = (x: number, y: number, dur = 1.0, amp = 30, wave = 160): void =>
    shockwaves.trigger(x, y, dur, amp, wave);

  const overlay = new Container();
  app.stage.addChild(overlay);

  // Title-screen demo black hole — uses the in-game hazards system. Spawned on title
  // entry, removed on title exit. No paintDemoBH duplication.
  let demoBhRef: import('./hazards').BlackHole | null = null;

  // Permanent music toggle button — top-right corner, visible in all states
  const musicBtn = new MusicToggleButton();
  app.stage.addChild(musicBtn.view);
  // Title-screen ambient nebula — drifting soft-glow blobs behind menu
  const titleNebula = new TitleNebula();
  app.stage.addChild(titleNebula.view);

  const overlayBg = new Graphics();
  overlay.addChild(overlayBg);
  const overlayTitle = new Text({
    text: '', style: { fontFamily: 'Courier New', fontSize: 56, fill: 0xffffff, fontWeight: 'bold' },
  });
  overlayTitle.anchor.set(0.5, 0.5);
  overlay.addChild(overlayTitle);
  const overlaySub = new Text({
    text: '', style: { fontFamily: 'Courier New', fontSize: 20, fill: 0xaaaaaa },
  });
  overlaySub.anchor.set(0.5, 0.5);
  overlay.addChild(overlaySub);
  const lbText = new Text({
    text: '', style: { fontFamily: 'Courier New', fontSize: 16, fill: 0xffcc00 },
  });
  lbText.anchor.set(0.5, 0);
  overlay.addChild(lbText);

  const achBanner = new AchievementBanner(app.stage);

  const tutText = new Text({
    text: '',
    style: {
      fontFamily: 'Courier New', fontSize: 18, fill: 0xeeffff, align: 'center',
      fontWeight: 'bold', letterSpacing: 2,
      dropShadow: { color: 0x00ffcc, alpha: 0.85, distance: 0, angle: 0, blur: 6 } as any,
    },
  });
  tutText.anchor.set(0.5, 0);
  app.stage.addChild(tutText);

  const screens = new Screens(app, () => screens.close());
  screens.view.scale.set(MOBILE_ZOOM);

  // Title-screen demo flight — ghost ship doing lissajous loops behind hero title
  // Use tier-2 silhouette for richer profile (under-wing weapon pods + secondary fins).
  const demoShip = new Sprite(SPRITES.playerP1Tiers[2]);
  demoShip.anchor.set(0.5, 0.5);
  demoShip.visible = false;
  demoShip.alpha = 0.95;
  let demoGlow: GlowFilter | null = null;
  try {
    demoGlow = new GlowFilter({ distance: 14, outerStrength: 2.0, innerStrength: 0.4, color: 0x00ffff, quality: 0.5 });
    demoShip.filters = [demoGlow as any];
  } catch {}
  app.stage.addChild(demoShip);
  const DEMO_TRAIL_MAX = 40;
  const demoRopePoints: Point[] = [];
  for (let i = 0; i < DEMO_TRAIL_MAX; i++) demoRopePoints.push(new Point(0, 0));
  const demoTrailTex = getTrailTextures('comet');
  const demoTrailMid = new MeshRope({ texture: demoTrailTex.mid, points: demoRopePoints });
  const demoTrailCore = new MeshRope({ texture: demoTrailTex.core, points: demoRopePoints });
  demoTrailMid.tint = 0x00ffff; demoTrailMid.alpha = 0.7; demoTrailMid.blendMode = 'add';
  demoTrailCore.tint = 0xffffff; demoTrailCore.alpha = 0.95; demoTrailCore.blendMode = 'add';
  demoTrailMid.visible = demoTrailCore.visible = false;
  app.stage.addChild(demoTrailMid);
  app.stage.addChild(demoTrailCore);
  const demoShieldRing = new Graphics();
  demoShieldRing.blendMode = 'add';
  demoShieldRing.visible = false;
  app.stage.addChild(demoShieldRing);

  // P2 demo bot sprite + trail
  const DEMO_TRAIL_MAX2 = 30;
  const demoRopePoints2: Point[] = [];
  for (let i = 0; i < DEMO_TRAIL_MAX2; i++) demoRopePoints2.push(new Point(0, 0));
  const demoTrailMid2 = new MeshRope({ texture: demoTrailTex.mid, points: demoRopePoints2 });
  const demoTrailCore2 = new MeshRope({ texture: demoTrailTex.core, points: demoRopePoints2 });
  demoTrailMid2.tint = 0xff44cc; demoTrailMid2.alpha = 0.7; demoTrailMid2.blendMode = 'add';
  demoTrailCore2.tint = 0xff88ee; demoTrailCore2.alpha = 0.95; demoTrailCore2.blendMode = 'add';
  demoTrailMid2.visible = demoTrailCore2.visible = false;
  app.stage.addChild(demoTrailMid2);
  app.stage.addChild(demoTrailCore2);
  const demoShip2 = new Sprite(SPRITES.playerP1Tiers[1]);
  demoShip2.anchor.set(0.5, 0.5);
  demoShip2.tint = 0xff44cc;
  demoShip2.visible = false;
  try { demoShip2.filters = [new GlowFilter({ distance: 14, outerStrength: 2.0, innerStrength: 0.4, color: 0xff44cc, quality: 0.5 }) as any]; } catch {}
  app.stage.addChild(demoShip2);

  // Title-screen subscreen buttons (rendered as Pixi text + click regions)
  type TitleBtn = { x: number; y: number; w: number; h: number; lbl: string; fn: () => void };
  let titleBtns: TitleBtn[] = [];
  let titleFocusIdx = 0;
  const titleBtnLayer = new Container();
  app.stage.addChild(titleBtnLayer);
  const titleFocusRing = new Graphics();
  app.stage.addChild(titleFocusRing);

  // Demo loop state — restored after a partial Phase-1 module extraction.
  // Demo asteroids are now real Enemy entries from EnemyManager (tagged .demo=true)
  // so they share update/draw/collision with the in-game system.
  let demoT = 0;
  const demoTrailPoints: Array<{ x: number; y: number }> = [];
  let demoX = 0, demoY = 0, demoVx = 0, demoVy = 0;
  let demoTargetX = 0, demoTargetY = 0;
  let demoTargetCD = 0;
  let demoInited = false;
  let demoFireCD = 0;
  let demoNovaCD = 4 + Math.random() * 6;
  let demoShieldOn = false;
  let demoShieldCD = 0;
  const demoRocks: Enemy[] = [];
  let demoRockSpawnCD = 0;
  const demoEnemies: Enemy[] = [];
  let demoEnemyWaveCD = 3.0;
  let demoEnemyBatch = 0;
  // P2 demo bot
  let demoX2 = 0, demoY2 = 0, demoVx2 = 0, demoVy2 = 0;
  let demoTargetX2 = 0, demoTargetY2 = 0, demoTargetCD2 = 0;
  let demoFireCD2 = 0;
  let demoNovaCD2 = 4 + Math.random() * 6;
  let demoShieldOn2 = false;
  let demoShieldCD2 = 0;
  let demoInited2 = false;
  const demoTrailPoints2: Array<{ x: number; y: number }> = [];
  let demoBHX = 0, demoBHY = 0;

  function rebuildTitleBtns(): void {
    titleBtnLayer.removeChildren();
    titleBtns = [];
    if (G.state !== 'title') return;

    type Tier = 'primary' | 'secondary' | 'toggle';
    type Item = { lbl: string; sub?: string; fn: () => void; tier: Tier; on?: boolean };

    const isMobile = isTouchDevice || G.H < 500;
    const gap = isMobile ? 6 : 10;

    function buildBtn(bx: number, by: number, bw: number, bh: number, it: Item): void {
      const isPrimary = it.tier === 'primary';
      const isToggle  = it.tier === 'toggle';
      const accent = isPrimary ? 0x00ffcc : isToggle ? (it.on ? 0x00ff66 : 0x4488ff) : 0x88aaff;
      const lblSize = isPrimary ? (isMobile ? 17 : 22) : isToggle ? (isMobile ? 12 : 14) : (isMobile ? 14 : 18);

      const btn = new Container();
      btn.x = bx + bw / 2;
      btn.y = by + bh / 2;
      titleBtnLayer.addChild(btn);

      let btnGlow: GlowFilter | null = null;
      try {
        btnGlow = new GlowFilter({ distance: 12, outerStrength: 1.0, innerStrength: 0.2, color: accent, quality: 0.4 });
        btn.filters = [btnGlow as any];
      } catch {}

      const bg = new Graphics();
      btn.addChild(bg);
      const sheen = new Graphics();
      sheen.roundRect(-bw / 2 + 2, -bh / 2 + 2, bw - 4, 10, 5);
      sheen.fill({ color: accent, alpha: 0.10 });
      btn.addChild(sheen);
      const strip = new Graphics();
      strip.roundRect(-bw / 2 + 4, -bh / 2 + 5, 4, bh - 10, 2);
      strip.fill({ color: accent });
      btn.addChild(strip);

      const lbl = new Text({
        text: it.lbl,
        style: {
          fontFamily: 'Courier New', fontSize: lblSize,
          fill: isPrimary ? 0xffffff : 0xddeeff,
          fontWeight: 'bold', letterSpacing: 2,
          dropShadow: { color: 0x000000, alpha: 0.85, distance: 2, angle: Math.PI / 4, blur: 3 },
        },
      });
      lbl.anchor.set(0, 0.5);
      lbl.x = -bw / 2 + 20;
      lbl.y = it.sub ? -7 : 0;
      btn.addChild(lbl);

      if (it.sub) {
        const sub = new Text({
          text: it.sub,
          style: { fontFamily: 'Courier New', fontSize: isMobile ? 10 : 12, fill: accent, fontWeight: 'bold', letterSpacing: 3 },
        });
        sub.anchor.set(0, 0.5);
        sub.x = -bw / 2 + 20;
        sub.y = 10;
        btn.addChild(sub);
      }

      const arrow = new Text({ text: '›', style: { fontFamily: 'Courier New', fontSize: isMobile ? 18 : 24, fill: accent, fontWeight: 'bold' } });
      arrow.anchor.set(1, 0.5);
      arrow.x = bw / 2 - 12;
      btn.addChild(arrow);

      const applyHover = (h: boolean): void => {
        bg.clear();
        bg.roundRect(-bw / 2, -bh / 2, bw, bh, 10);
        bg.fill({ color: h ? 0x0d0d28 : 0x070716, alpha: h ? 0.95 : 0.88 });
        bg.stroke({ width: h ? 2.5 : 1.5, color: accent, alpha: h ? 1 : 0.55 });
        btn.scale.set(h ? 1.04 : 1);
        arrow.x = bw / 2 - (h ? 8 : 12);
        if (btnGlow) btnGlow.outerStrength = h ? 2.6 : 1.0;
      };
      applyHover(false);

      btn.eventMode = 'static';
      btn.cursor = 'pointer';
      btn.hitArea = new Rectangle(-bw / 2, -bh / 2, bw, bh);
      btn.on('pointerover', () => { applyHover(true); SFX.menuHover(); });
      btn.on('pointerout',  () => applyHover(false));
      btn.on('pointertap',  () => { SFX.menuClick(); it.fn(); });

      titleBtns.push({ x: bx, y: by, w: bw, h: bh, lbl: it.lbl, fn: it.fn });
    }

    const primaryItems: Item[] = [
      { lbl: 'DÉMARRER',       sub: 'partie standard', fn: () => startGame(),     tier: 'primary' },
      { lbl: 'DÉFI QUOTIDIEN', sub: dailyKey(),         fn: () => startGame(true), tier: 'primary' },
    ];
    const secondaryItems: Item[] = [
      { lbl: 'AMÉLIORATIONS', sub: `${getShards()} ÉCLATS`, fn: () => screens.open('upgrades'),    tier: 'secondary' },
      { lbl: 'SUCCÈS',        fn: () => screens.open('achievements'), tier: 'secondary' },
      { lbl: 'TRAÎNÉES',      fn: () => screens.open('trails'),       tier: 'secondary' },
      { lbl: 'RÉGLAGES',      fn: () => screens.open('settings'),     tier: 'secondary' },
      { lbl: 'CLASSEMENT',   fn: () => {
          const all = {
            campaign: getLBByKey(LB_KEYS.campaign),
            endless:  getLBByKey(LB_KEYS.endless),
            daily:    getLBByKey(`ns_lb_daily_${dailyKey()}`),
          };
          screens.lbData = { rows: all.campaign, mode: 'CAMPAGNE', tab: 'campaign', all };
          screens.open('leaderboard');
        }, tier: 'secondary' },
    ];
    const toggleItems: Item[] = [
      {
        lbl: 'MODE', sub: G.endless ? 'SANS FIN' : 'CAMPAGNE 25v',
        fn: () => { G.endless = !G.endless; localStorage.setItem('ns_endless', G.endless ? '1' : '0'); rebuildTitleBtns(); },
        tier: 'toggle', on: G.endless,
      },
      ...(!isTouchDevice ? [{
        lbl: 'COOP', sub: coop.enabled ? 'ACTIVÉ' : 'DÉSACTIVÉ',
        fn: () => {
          coop.enabled = !coop.enabled;
          localStorage.setItem('ns_coop_pixi', coop.enabled ? '1' : '0');
          if (coop.enabled && !player2) player2 = new Player2(world, G.W * 0.4, G.H / 2);
          if (!coop.enabled && player2) { player2.view.parent?.removeChild(player2.view); player2.view.destroy({ children: true }); player2 = null; }
          rebuildTitleBtns();
        },
        tier: 'toggle' as Tier, on: coop.enabled,
      }] : []),
    ];

    if (isMobile) {
      // ── Mobile: centré, grille 2 colonnes ────────────────────────────────
      const btnW = Math.min(G.W - 16, 420);
      const colX = (G.W - btnW) / 2;
      const half  = (btnW - gap) / 2;
      const avail = G.H - 16 - 4 * gap;
      const pxH = Math.max(44, Math.min(60, avail * 0.30));
      const scH = Math.max(32, Math.min(46, avail * 0.22));
      const tgH = Math.max(28, Math.min(38, avail * 0.17));
      const numSecRows = Math.ceil(secondaryItems.length / 2);
      const totalH = primaryItems.length * (pxH + gap) + numSecRows * (scH + gap) + toggleItems.length * (tgH + gap) - gap;
      let y = Math.max(8, (G.H - totalH) / 2);

      for (const it of primaryItems) { buildBtn(colX, y, btnW, pxH, it); y += pxH + gap; }
      for (let r = 0; r < numSecRows; r++) {
        buildBtn(colX,              y, half, scH, secondaryItems[r * 2]);
        if (secondaryItems[r * 2 + 1]) buildBtn(colX + half + gap, y, half, scH, secondaryItems[r * 2 + 1]);
        y += scH + gap;
      }
      for (const it of toggleItems) {
        buildBtn(colX, y, btnW, tgH, it);
        y += tgH + gap;
      }
    } else {
      // ── Desktop: colonne droite, hauteurs hiérarchisées ──────────────────
      const btnW = Math.min(380, Math.floor(G.W * 0.32));
      const colX = G.W - btnW - 48;
      const pxH = 68, scH = 50, tgH = 38;
      const totalH = primaryItems.length   * (pxH + gap)
                   + secondaryItems.length * (scH + gap)
                   + toggleItems.length    * (tgH + gap) - gap + 16;
      let y = Math.max(16, G.H / 2 - totalH / 2);

      for (const it of primaryItems)   { buildBtn(colX, y, btnW, pxH, it); y += pxH + gap; }
      y += 8;
      for (const it of secondaryItems) { buildBtn(colX, y, btnW, scH, it); y += scH + gap; }
      y += 8;
      for (const it of toggleItems)    { buildBtn(colX, y, btnW, tgH, it); y += tgH + gap; }


    }
  }

  let lives = 3;
  let respawnT = 0;
  let weaponIdx = 0;
  // Comet spawn timer — fires a passing comet every 6-10s on non-boss waves
  let cometT = 0;
  let cometNext = 6 + Math.random() * 4;
  let specialCharges = [2, 1, 1];
  // Leaderboard "you just scored here" highlight — idx of the row to mark, t = remaining seconds
  const lbHighlight = { idx: -1, t: 0 };
  let combo = 0;
  let comboTimer = 0;
  // Active-effect timers (countdown each frame; 0 = inactive)
  const fx = { synergy: 0, slowField: 0, shieldSurge: 0 };
  let lastWeaponPickupT = -999;
  let lastAutoUpgradeWeaponIdx = -1;
  let bashCooldown = 0;
  // Charge attack state — t accumulates while R is held, released triggers volley
  const charge = { t: 0, released: 0 };
  let specialIdx = 0;
  const weaponAddons = new WeaponAddons();
  let missileTier = 0;
  let ringActive = false;
  // Per-run accumulators — bundled so future GameContext refactor is a single rename.
  const runStats = {
    bossKills: 0,
    wavesCleared: 0,
    ramKills: 0,
    asteroidsKilled: 0,
    phoenixUsed: false,
  };
  let dmgMul = 1;
  let magnetMul = 1;
  let tutStep = isTutorialDone() ? 0 : 1;
  const scoreZones = new ScoreZones(world);
  let scoreMul = 1;
  // Camera (shake + boss-entry zoom) — both apply transform to app.stage
  const camera = new Camera(app.stage);
  const triggerZoom = (dur = 0.6): void => camera.zoom(dur);
  // Title-screen intro animation — runs once on boot, replays whenever returning to title
  let titleIntroT = 1.0;
  let lastTitleSeen = false;
  // Wave-clear RGB-split screen wipe (3 chromatic-offset bars sweeping across)
  const wipeOverlay = new WipeOverlay();
  app.stage.addChild(wipeOverlay.view);
  const triggerWipe = (dur = 0.55): void => wipeOverlay.trigger(dur);
  const shake = (mag: number, dur: number): void => camera.shake(mag, dur);
  let waveKills = 0;
  const STREAK_MARKS = [10, 25, 50];
  let streakHit: Record<number, boolean> = {};

  wave.cleared = (n, noDmg) => {
    if ([5, 10, 15, 20, 25].includes(n)) {
      const s = wave.stats;
      console.log(
        `[TELEMETRY] Wave ${n}` +
        ` | clear=${s.clearTime?.toFixed(1)}s` +
        ` | dmg=${s.dmgTaken}` +
        ` | wLvl=${s.wIdxStart}→${weaponIdx}` +
        ` | kills=${s.kills}` +
        ` | acc=${s.shotsFired ? ((s.shotsHit / s.shotsFired) * 100).toFixed(0) : '?'}%`
      );
    }
    runStats.wavesCleared = n;
    fireAch({ kind: 'wave_clear', n });
    if (noDmg) fireAch({ kind: 'no_dmg_wave' });
    waveKills = 0;
    streakHit = {};
    runWaveClearCeremony(n, { hazards, triggerZoom, triggerWipe }, G.W, G.H);
  };

  buildWave(1, enemies);
  G.state = 'title';

  function fireSpecial(idx: number): void {
    if (idx === 0) {
      // NOVA BOMB — clears enemies, asteroids AND enemy bullets in a 384px radius
      const NOVA_R = 384;
      const NOVA_R2 = NOVA_R * NOVA_R;
      for (const en of enemies.enemies) {
        if (!en.alive) continue;
        const dx = en.x - player.x, dy = en.y - player.y;
        if (dx * dx + dy * dy < NOVA_R2) {
          if (en.kind === 'boss') en.hp -= en.maxHp * 0.22;
          else if (en.kind === 'miniboss') en.hp -= en.maxHp * 0.45;
          else en.hp -= 999; // covers standard enemies + asteroids (asteroids have low hp)
          if (en.hp <= 0) {
            hud.score += en.val;
            particles.burst(en.x, en.y, 24, en.color, 380, 0.9, Math.PI * 2, 0, 2, 5);
            const drop = rollPowerup(en.kind);
            if (drop) powerups.spawn(drop, en.x, en.y);
            enemies.kill(en, true);
            combo += 1; comboTimer = 2.6; hud.combo = combo;
          }
        }
      }
      // Wipe enemy bullets in the same radius
      for (const eb of ebullets.pool) {
        if (!eb.alive) continue;
        const dx = eb.x - player.x, dy = eb.y - player.y;
        if (dx * dx + dy * dy < NOVA_R2) {
          particles.burst(eb.x, eb.y, 4, 0xff44cc, 220, 0.3);
          ebullets.kill(eb);
        }
      }
      // === NUCLEAR BLAST ===
      const px = player.x, py = player.y;
      // Telegraph ring — fast expansion + lingering glow
      novaRingX = px; novaRingY = py; novaRingR = NOVA_R; novaRingT = 0.85;
      // Particle layers: white-hot inner core (fast), amber middle (med), red ember halo (slow drift)
      particles.burst(px, py, 180, 0xffffff, 1200, 0.45, Math.PI * 2, 0, 4, 10);  // hot core
      particles.burst(px, py, 220, 0xffcc44, 900,  0.85, Math.PI * 2, 0, 3, 9);   // bright body
      particles.burst(px, py, 140, 0xff6622, 620,  1.40, Math.PI * 2, 0, 2.4, 7); // amber ember
      // Double shockwave — instant inner + delayed outer for that "double-tap" nuclear punch
      triggerShockwave(px, py, 1.4, 48, 240);
      triggerFlash(0.92, 0xffffe8);
      shake(34, 0.55);
      SFX.boom(); SFX.special();
      setTimeout(() => {
        triggerShockwave(px, py, 1.6, 32, 320);
        triggerFlash(0.55, 0xffcc66);
        shake(22, 0.45);
        SFX.boom();
        // Mushroom-cloud puff — slow rising smoke
        particles.burst(px, py - 20, 80, 0xaa6622, 220, 1.6, Math.PI, -Math.PI, 2.2, 8);
        particles.burst(px, py - 40, 60, 0x886644, 180, 1.8, Math.PI, -Math.PI, 1.8, 7);
      }, 150);
      // Tail crash — high crystalline crack 0.3s after the boom
      setTimeout(() => {
        SFX.boom();
        triggerFlash(0.30, 0xfff8aa);
      }, 320);
    } else if (idx === 1) {
      // SHIELD SURGE
      fx.shieldSurge = 2.5;
      player.shield = player.maxShield;
      particles.burst(player.x, player.y, 40, 0x4488ff, 320, 0.8, Math.PI * 2, 0, 2, 6);
      SFX.special();
    } else if (idx === 2) {
      // SLOW FIELD + 20-shot ring burst
      fx.slowField = 2.0;
      for (let i = 0; i < 20; i++) {
        const a = (i / 20) * Math.PI * 2;
        bullets.spawn({
          x: player.x, y: player.y,
          vx: Math.cos(a) * 950, vy: Math.sin(a) * 950,
          dmg: 30, r: 5, color: 0xffff44, pierce: true, pierceCt: 5,
        });
      }
      particles.burst(player.x, player.y, 30, 0xffff44, 320, 0.7);
      SFX.special();
    }
  }

  function startGame(isDaily = false) {
    G.daily = isDaily;
    if (isDaily) { setDailyRNG(); } else { setMathRNG(); }
    G.state = 'playing';
    hud.score = 0;
    lives = 3;
    weaponIdx = 0;
    lastAutoUpgradeWeaponIdx = -1;
    specialCharges = [2, 1, 1];
    respawnT = 0;
    runStats.bossKills = 0;
    runStats.wavesCleared = 0;
    runStats.ramKills = 0;
    runStats.asteroidsKilled = 0;
    runStats.phoenixUsed = false;
    dmgMul = dmgMultFromUpgrades();
    magnetMul = magnetRadiusMult();
    player.reset(G.W * 0.25, G.H / 2, true); // instant: skip arrival animation on fresh start
    player.setVisible(true);
    applyUpgradesToPlayer(player);
    tutStep = isTutorialDone() ? 0 : 1;
    hazards.clearAll();
    for (const e of enemies.enemies) enemies.kill(e, false);
    enemies.enemies.length = 0;
    for (const b of bullets.pool) bullets.kill(b);
    for (const eb of ebullets.pool) ebullets.kill(eb);
    while (powerups.pool.length) {
      const p = powerups.pool.pop()!;
      p.view.parent?.removeChild(p.view);
      p.view.destroy();
    }
    buildWave(1, enemies, 0, 0);
    if (isDaily) wave.banner = { text: `DÉFI DU JOUR : ${dailyKey()}`, color: 0xffcc00, t: 200 };
    showMobileInstructions();
  }

  window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyR' && (G.state === 'gameover' || G.state === 'title' || G.state === 'victory')) startGame();
    if (e.code === 'KeyM' && G.state === 'title') {
      G.endless = !G.endless;
      localStorage.setItem('ns_endless', G.endless ? '1' : '0');
    }
    if (e.code === 'KeyN') toggleMusic();

    // Co-op revival (Tab/Enter)
    if (G.state === 'playing' && coop.enabled && player2) {
      if (e.code === 'Tab') {
        e.preventDefault();
        if (player2.dead) {
          player2.dead = false;
          player2.view.visible = true;
          player2.x = player.x + 30; player2.y = player.y;
          player2.hp = Math.ceil(player2.maxHp * 0.5);
          player2.shield = 0;
          player2.invuln = 120;
          particles.burst(player2.x, player2.y, 30, 0xff44cc, 380, 0.7, Math.PI * 2, 0, 2, 5);
          wave.banner = { text: 'J2 RÉANIMÉ', color: 0xff44cc, t: 100 };
          SFX.special();
        }
      }
      if (e.code === 'Enter') {
        if (respawnT > 0 && !player2.dead) {
          respawnT = 0;
          player.x = player2.x - 30; player.y = player2.y;
          player.hp = Math.ceil(player.maxHp * 0.5);
          player.shield = 0;
          player.invuln = 120;
          particles.burst(player.x, player.y, 30, 0x00ffff, 380, 0.7, Math.PI * 2, 0, 2, 5);
          wave.banner = { text: 'J1 RÉANIMÉ', color: 0x00ffff, t: 100 };
          SFX.special();
        }
      }
    }
    if (e.code === 'KeyC' && G.state === 'title') {
      coop.enabled = !coop.enabled;
      localStorage.setItem('ns_coop_pixi', coop.enabled ? '1' : '0');
      if (coop.enabled && !player2) player2 = new Player2(world, G.W * 0.4, G.H / 2);
      if (!coop.enabled && player2) {
        player2.view.parent?.removeChild(player2.view);
        player2.view.destroy({ children: true });
        player2 = null;
      }
    }
    if (e.code === 'KeyT' && tutStep > 0) {
      tutStep = 0;
      markTutorialDone();
    }
    // Auto-advance tutorial steps
    if (tutStep === 1 && (e.code === 'KeyW' || e.code === 'KeyA' || e.code === 'KeyS' || e.code === 'KeyD')) tutStep = 2;
    if (tutStep === 3 && e.code === 'Space') tutStep = 4;
    if (tutStep === 4 && (e.code === 'ShiftLeft' || e.code === 'ShiftRight')) tutStep = 5;
    if (tutStep === 5 && e.code === 'KeyE') { tutStep = 0; markTutorialDone(); }
    if (e.code === 'Escape' && G.state === 'playing') G.state = 'paused';
    else if (e.code === 'Escape' && G.state === 'paused') G.state = 'playing';
    if (e.code === 'KeyQ' && (G.state === 'paused' || G.state === 'gameover' || G.state === 'victory')) {
      hazards.clearAll();
      G.state = 'title';
      lastTitleSeen = false; // replay title intro
    }
    // Title menu keyboard navigation
    if (G.state === 'title' && !screens.current && titleBtns.length > 0) {
      if (e.code === 'ArrowDown' || e.code === 'KeyS') {
        titleFocusIdx = (titleFocusIdx + 1) % titleBtns.length;
        SFX.menuHover();
        e.preventDefault();
      } else if (e.code === 'ArrowUp' || e.code === 'KeyW') {
        titleFocusIdx = (titleFocusIdx - 1 + titleBtns.length) % titleBtns.length;
        SFX.menuHover();
        e.preventDefault();
      } else if (e.code === 'Enter' || e.code === 'NumpadEnter') {
        SFX.menuClick();
        titleBtns[titleFocusIdx]?.fn();
        e.preventDefault();
      }
    }
    if (G.state === 'playing') {
      if (e.code === 'Digit1') specialIdx = 0;
      else if (e.code === 'Digit2') specialIdx = 1;
      else if (e.code === 'Digit3') specialIdx = 2;
      else if (e.code === 'Enter') {
        // Special is right-click + Enter only; Space is reserved for brake.
        if (settings.godMode || specialCharges[specialIdx] > 0) {
          if (!settings.godMode) specialCharges[specialIdx] -= 1;
          fireSpecial(specialIdx);
        }
      }
      if (e.code === 'KeyE' && bashCooldown <= 0 && player.shield >= 3) {
        player.shield -= 3;
        bashCooldown = 4;
        let reflected = 0;
        for (const eb of ebullets.pool) {
          if (!eb.alive) continue;
          const dx = eb.x - player.x, dy = eb.y - player.y;
          if (dx * dx + dy * dy < 130 * 130) {
            const a = Math.atan2(-eb.vy, -eb.vx);
            bullets.spawn({
              x: eb.x, y: eb.y,
              vx: Math.cos(a) * Math.hypot(eb.vx, eb.vy) * 1.4,
              vy: Math.sin(a) * Math.hypot(eb.vx, eb.vy) * 1.4,
              dmg: 22, r: 4, color: 0x4488ff,
            });
            ebullets.kill(eb);
            reflected += 1;
          }
        }
        for (const en of enemies.enemies) {
          if (!en.alive) continue;
          if (en.kind === 'boss' || en.kind === 'miniboss') continue;
          const dx = en.x - player.x, dy = en.y - player.y;
          if (dx * dx + dy * dy < 130 * 130) {
            en.hp -= 45;
            en.hitFlashT = 8;
            const d = Math.hypot(dx, dy) || 1;
            en.vx += (dx / d) * 280;
            en.vy += (dy / d) * 280;
            if (en.hp <= 0) {
              hud.score += en.val;
              const drop = rollPowerup(en.kind);
              if (drop) powerups.spawn(drop, en.x, en.y);
              enemies.kill(en, true);
              combo += 1; comboTimer = 2.6; hud.combo = combo;
            }
          }
        }
        particles.burst(player.x, player.y, 36, 0x4488ff, 600, 0.7, Math.PI * 2, 0, 2, 6);
        SFX.special();
        if (reflected > 0) console.log(`[BASH] reflected ${reflected}`);
      }
      if (e.code === 'KeyR' && !e.repeat) charge.t = 0.001;
    }
  });
  window.addEventListener('pointerdown', (ev: PointerEvent) => {
    // Right-click: fire current special during play
    if (ev.button === 2 && G.state === 'playing') {
      if (settings.godMode || specialCharges[specialIdx] > 0) {
        if (!settings.godMode) specialCharges[specialIdx] -= 1;
        fireSpecial(specialIdx);
      }
      return;
    }
    if (screens.current) return;
    // Title-screen clicks are handled by Pixi-native pointertap on each button.
    if (G.state === 'title') return;
    if (tutStep === 1) tutStep = 2;
    if (tutStep === 2) tutStep = 3;
  });
  // Suppress browser context menu so right-click can be a game input
  window.addEventListener('contextmenu', (e) => e.preventDefault());
  // Mute audio when tab/window is hidden — suspend AudioContext entirely
  document.addEventListener('visibilitychange', () => setSuspended(document.hidden));
  window.addEventListener('blur', () => setSuspended(true));
  window.addEventListener('focus', () => setSuspended(false));
  window.addEventListener('keyup', (e) => {
    if (e.code === 'KeyR' && G.state === 'playing' && charge.t > 0) {
      if (charge.t >= 0.5) {
        bullets.spawn({
          x: player.x + Math.cos(player.aimAng) * 24,
          y: player.y + Math.sin(player.aimAng) * 24,
          vx: Math.cos(player.aimAng) * 1500,
          vy: Math.sin(player.aimAng) * 1500,
          dmg: 60, r: 8, color: 0x00ffff, pierce: true, pierceCt: 5,
        });
        particles.burst(player.x + Math.cos(player.aimAng) * 24, player.y + Math.sin(player.aimAng) * 24,
          24, 0x00ffff, 500, 0.6, Math.PI / 2, player.aimAng, 2, 5);
        SFX.special();
      }
      charge.t = 0;
      charge.released = 0;
    }
  });

  const fireTimer = { v: 0 };

  app.ticker.add((ticker) => {
    const dt = Math.min(0.05, ticker.deltaMS / 1000);
    G.frameCount += 1;
    pushMouseHistory();
    floats.update(dt);
    tickMobileInstructions(dt);

    // DEV — god mode keeps player at full HP/shield each frame and skips damage. Hits
    // still play VFX/SFX so the dev can see they're being hit; the player just doesn't die.
    // Touch special buttons — fire immediately on tap
    if (G.state === 'playing' && keys.touchSpecial >= 0) {
      const tsIdx = keys.touchSpecial;
      keys.touchSpecial = -1;
      if (settings.godMode || specialCharges[tsIdx] > 0) {
        if (!settings.godMode) specialCharges[tsIdx] -= 1;
        fireSpecial(tsIdx);
      }
    }

    if (settings.godMode && G.state === 'playing') {
      player.hp = player.maxHp;
      player.shield = player.maxShield;
      if (coop.enabled && player2) {
        player2.hp = player2.maxHp;
        player2.shield = player2.maxShield;
      }
    }

    // Title-state hazards + enemies tick — animates the demo BH (disk spin, dust
    // suction, hot patches) AND drives demo asteroid motion (Y-wall bounce, spiral
    // pull from BH) via the real systems. isBoss=true skips random hazard spawns.
    if (G.state === 'title') {
      hazards.update(dt, G.W, G.H, true, false);
      enemies.update(dt, G.W, G.H, { x: demoX, y: demoY });
      // Demo asteroids fall through the same gravity well as in-game ones
      for (const rk of demoRocks) {
        if (rk.alive) hazards.pullVelocity(rk, dt, 0.4);
      }
    }

    // Engine sound default-mute on any non-playing state (pause/title/gameover);
    // demo + playing branches override below with real speed.
    if (G.state !== 'playing') setEngineState(0, false, false);
    // Music toggle button position — top-right, always visible
    musicBtn.setPosition(G.W - 50, 12);
    // Nebula clouds only visible on title
    if (G.state !== 'title') titleNebula.setVisible(false);

    // Camera shake + boss-entry zoom — both apply transform to app.stage
    camera.tick(dt, G.W, G.H);

    // Advance shockwaves; rebuilds stage.filters internally when set changes
    shockwaves.tick(dt);

    // Wave-clear RGB-split wipe (3 chromatic-offset bars sweeping across)
    wipeOverlay.tick(dt, G.W, G.H);

    // Update flash overlay (full-screen additive flash)
    flashOverlay.tick(dt, G.W, G.H);

    stars.setBoostStretch(keys.boost && player.shield > 0 ? 1 : 0);
    stars.update(dt, G.W, G.H, wave.wave || 1);

    // Animate film grain (reseed per frame for shimmer effect)
    filmGrain.seed = Math.random();

    // Biome color grading — gentle saturate+hue shift per tier
    applyBiomeGrade(colorMatrix, wave.wave);

    // Low-HP red vignette — pulses faster as HP drops
    vignette.tick(dt, Math.max(0, Math.min(1, player.hp / player.maxHp)), G.W, G.H);
    terrain.update(dt, wave.wave || 1);
    terrain.draw(G.W, G.H, wave.wave || 1);
    // Particles slow down when player is near a black hole — fits the time-dilation feel
    const bhParticleScale = (G.state === 'playing' && player)
      ? hazards.bhTimeScale(player.x, player.y) : 1;
    particles.update(dt * bhParticleScale);

    titleBtnLayer.visible = (G.state === 'title' && !screens.current);
    titleFocusRing.visible = titleBtnLayer.visible;
    if (titleBtns.length === 0 && G.state === 'title') rebuildTitleBtns();
    // Focus ring around the keyboard-selected button — pulses subtly
    if (titleFocusRing.visible && titleBtns.length > 0) {
      if (titleFocusIdx >= titleBtns.length) titleFocusIdx = 0;
      drawTitleFocusRing(titleFocusRing, titleBtns[titleFocusIdx]);
    } else {
      drawTitleFocusRing(titleFocusRing, null);
    }
    if (G.state !== 'title' && titleBtns.length > 0) {
      titleBtnLayer.removeChildren();
      titleBtns = [];
    }
    if (G.state !== 'title') {
      lastTitleSeen = false;
      if (demoShip.visible) {
        demoShip.visible = false;
        demoTrailMid.visible = false;
        demoTrailCore.visible = false;
        demoTrailPoints.length = 0;
        demoShieldRing.visible = false;
        demoShieldRing.clear();
        // Clean up any drifting demo asteroids
        for (const rk of demoRocks) {
          if (rk.alive) enemies.kill(rk, false);
        }
        demoRocks.length = 0;
        for (const en of demoEnemies) {
          if (en.alive) enemies.kill(en, false);
        }
        demoEnemies.length = 0;
        demoShip2.visible = false;
        demoTrailMid2.visible = false;
        demoTrailCore2.visible = false;
        demoTrailPoints2.length = 0;
        demoInited2 = false;
      }
      // Despawn the demo BH (real hazards entry) when leaving title
      if (demoBhRef) {
        const idx = hazards.blackHoles.indexOf(demoBhRef);
        if (idx >= 0) {
          demoBhRef.view.parent?.removeChild(demoBhRef.view);
          demoBhRef.view.destroy();
          demoBhRef.diskView.parent?.removeChild(demoBhRef.diskView);
          demoBhRef.diskView.destroy();
          demoBhRef.infallView.parent?.removeChild(demoBhRef.infallView);
          demoBhRef.infallView.destroy();
          hazards.blackHoles.splice(idx, 1);
        }
        demoBhRef = null;
      }
    }
    if (G.state === 'title') {
      overlay.visible = !screens.current;
      // Calm title-screen BGM — wave=0 routes to 'title' section (pad+arp only).
      // Keep playing through subscreens (settings/upgrades/etc) — only pause kills it.
      tickBGM(dt, 0, false, false);
      // Demo ship engine + friction — same audio model as in-game, driven by demoVx/Vy.
      // Muted while a subscreen is open (already paused via tickBGM).
      {
        const sp = Math.hypot(demoVx, demoVy);
        const alive = !screens.current;
        setEngineState(sp / 700, false, alive);
      }
      // Title-screen demo black hole — uses the real hazards system. Spawn lazily on
      // first frame in title; reposition by mutating the spawned BH's coordinates.
      if (demoBHX === 0) {
        demoBHX = G.W * 0.72;
        demoBHY = G.H * 0.40;
      }
      demoBHX += Math.sin(demoT * 0.20) * 0.4;
      demoBHY += Math.cos(demoT * 0.16) * 0.35;
      if (!demoBhRef) {
        hazards.spawnBlackHole(G.W, G.H, true);
        demoBhRef = hazards.blackHoles[hazards.blackHoles.length - 1];
      }
      // Sync demo-drift position into the real BH each frame (and hide when subscreen open)
      demoBhRef.x = demoBHX;
      demoBhRef.y = demoBHY;
      demoBhRef.view.x = demoBHX;
      demoBhRef.view.y = demoBHY;
      demoBhRef.diskView.x = demoBHX;
      demoBhRef.diskView.y = demoBHY;
      demoBhRef.infallView.x = demoBHX;
      demoBhRef.infallView.y = demoBHY;
      demoBhRef.view.visible = !screens.current;
      demoBhRef.diskView.visible = !screens.current;
      demoBhRef.infallView.visible = !screens.current;
      // Replay intro on entering title state
      if (!lastTitleSeen) {
        titleIntroT = 1.0;
        lastTitleSeen = true;
      }
      if (titleIntroT > 0) titleIntroT = Math.max(0, titleIntroT - dt / 0.8);
      const introT = 1 - titleIntroT; // 0..1 ease-out
      const introEased = 1 - Math.pow(1 - introT, 3);
      // Drifting nebula clouds — visibly moving + breathing soft-glow blobs behind the menu
      titleNebula.setVisible(!screens.current);
      titleNebula.render(G.W, G.H, dt, introEased);
      // Two-tone vertical gradient backdrop
      overlayBg.clear();
      overlayBg.rect(0, 0, G.W, G.H);
      overlayBg.fill({ color: 0x02040c, alpha: 0.78 });
      // Left vertical accent line (flush against hero panel) — animates in
      const accentH = G.H * 0.65 * introEased;
      const accentY = G.H * 0.18 + (G.H * 0.65 - accentH) * 0.5;
      overlayBg.rect(G.W * 0.10 - 22, accentY, 3, accentH);
      overlayBg.fill({ color: 0x00ffcc, alpha: 0.7 * introEased });

      // NOVA STRIKE — big neon hero, top-left aligned with stats card. Animated entrance.
      overlayTitle.text = 'NOVA\nSTRIKE';
      overlayTitle.anchor.set(0, 0);
      overlayTitle.x = G.W * 0.10 - (1 - introEased) * 60; // slides in from left
      overlayTitle.y = G.H * 0.20;
      overlayTitle.style.fontSize = 92;
      overlayTitle.style.fill = 0xffffff;
      overlayTitle.style.fontWeight = 'bold';
      overlayTitle.style.letterSpacing = 6;
      overlayTitle.style.lineHeight = 88;
      overlayTitle.style.dropShadow = {
        color: 0x00ffcc, alpha: 0.9 * introEased, distance: 0, angle: 0, blur: 18 + (1 - introEased) * 22,
      } as any;
      overlayTitle.alpha = introEased;

      // Subtitle / bindings — bottom strip
      overlaySub.text = `souris viser & tirer  ·  clic droit spécial  ·  Espace freiner  ·  Maj boost  ·  E choc  ·  R maintenu charge`;
      overlaySub.anchor.set(0.5, 0.5);
      overlaySub.x = G.W / 2; overlaySub.y = G.H - 32;
      overlaySub.style.fontSize = 13;
      overlaySub.style.fill = 0x6688aa;
      overlaySub.style.letterSpacing = 2;

      lbText.visible = false;
      // Fade-in title button column + stats card together
      titleBtnLayer.alpha = introEased;
      titleBtnLayer.x = (1 - introEased) * 40; // slides in slightly from the right

      // Demo shield aura — pulsing cyan bubble around ship while demoShieldOn
      demoShieldRing.clear();
      if (demoShieldOn && !screens.current) {
        demoShieldRing.visible = true;
        const pulse = 0.7 + 0.3 * Math.sin(Date.now() * 0.018);
        demoShieldRing.circle(demoX, demoY, 38);
        demoShieldRing.fill({ color: 0x4488ff, alpha: 0.10 * pulse * introEased });
        demoShieldRing.circle(demoX, demoY, 32);
        demoShieldRing.stroke({ width: 2.4, color: 0x4488ff, alpha: 0.85 * pulse * introEased });
        demoShieldRing.circle(demoX, demoY, 28);
        demoShieldRing.stroke({ width: 1, color: 0xaaeeff, alpha: 0.55 * pulse * introEased });
      } else {
        demoShieldRing.visible = false;
      }

      // Tick bullets so demo show-off bullets actually move + expire
      bullets.update(dt, G.W, G.H);

      // Demo bullet → asteroid collision: ship has fun blowing them up
      for (const b of bullets.pool) {
        if (!b.alive) continue;
        for (let i = demoRocks.length - 1; i >= 0; i--) {
          const rk = demoRocks[i];
          const ddx = b.x - rk.x, ddy = b.y - rk.y;
          if (ddx * ddx + ddy * ddy < 22 * 22) {
            // Boom — particles + remove rock + kill bullet (unless piercing)
            particles.burst(rk.x, rk.y, 18, 0xffaa00, 380, 0.7, Math.PI * 2, 0, 2, 5);
            particles.burst(rk.x, rk.y, 8, 0xffffff, 220, 0.4);
            SFX.hit();
            SFX.boom();
            enemies.kill(rk, false);
            demoRocks.splice(i, 1);
            if (b.pierce) {
              b.pierceCt -= 1;
              if (b.pierceCt <= 0) bullets.kill(b);
            } else {
              bullets.kill(b);
            }
            break;
          }
        }
      }

      // Demo ship — player-like physics: target-follow with acceleration + inertia + laser dodge
      demoShip.visible = !screens.current;
      demoTrailMid.visible = !screens.current;
      demoTrailCore.visible = !screens.current;
      demoT += dt;

      // Cycle the demo ship through tier sprites every ~5s to showcase progression
      const demoTier = Math.floor(demoT / 5) % SPRITES.playerP1Tiers.length;
      const demoTex = SPRITES.playerP1Tiers[demoTier];
      if (demoShip.texture !== demoTex) demoShip.texture = demoTex;

      // Demo asteroid spawner — uses the real EnemyManager pipeline. Tagged .demo=true
      // so we can distinguish them from in-game asteroids; motion + render handled by
      // enemies.update during title state.
      demoRockSpawnCD -= dt;
      if (demoRockSpawnCD <= 0 && demoRocks.length < 4) {
        demoRockSpawnCD = 2.0 + Math.random() * 2.5;
        const startY = G.H * 0.30 + Math.random() * G.H * 0.40;
        const sz = 1 + Math.floor(Math.random() * 2);   // sz 1 or 2 (small)
        const ast = enemies.spawnAsteroidAt(
          G.W + 60, startY, sz,
          -(60 + Math.random() * 100),
          (Math.random() - 0.5) * 30,
        );
        (ast as { demo?: boolean }).demo = true;
        demoRocks.push(ast);
      }
      // Off-screen cleanup — enemies.update handles motion + Y-wall bounce automatically
      for (let i = demoRocks.length - 1; i >= 0; i--) {
        const rk = demoRocks[i];
        if (!rk.alive || rk.x < -80) {
          if (rk.alive) enemies.kill(rk, false);
          demoRocks.splice(i, 1);
        }
      }

      // Demo combat enemies — drones/fighters/scouts, looping waves, not spoiling bosses
      demoEnemyWaveCD -= dt;
      if (demoEnemyWaveCD <= 0 && demoEnemies.filter(e => e.alive).length === 0) {
        demoEnemyWaveCD = 14 + Math.random() * 8;
        const kinds = (['drone', 'fighter', 'scout'] as const);
        const kind = kinds[demoEnemyBatch % kinds.length];
        demoEnemyBatch++;
        const count = kind === 'fighter' ? 3 : 4;
        for (let i = 0; i < count; i++) {
          const en = enemies.spawn(kind, 1);
          en.y = G.H * 0.15 + (G.H * 0.70 / Math.max(1, count - 1)) * i;
          en.vx = -(55 + Math.random() * 35);
          en.vy = (Math.random() - 0.5) * 15;
          demoEnemies.push(en);
        }
      }
      for (let i = demoEnemies.length - 1; i >= 0; i--) {
        const en = demoEnemies[i];
        if (!en.alive || en.x < -80) {
          if (en.alive) enemies.kill(en, false);
          demoEnemies.splice(i, 1);
        }
      }
      // Demo bullet → combat enemy collision
      for (const b of bullets.pool) {
        if (!b.alive) continue;
        for (let i = demoEnemies.length - 1; i >= 0; i--) {
          const en = demoEnemies[i];
          if (!en.alive) continue;
          const ddx = b.x - en.x, ddy = b.y - en.y;
          const hitR = en.radius + b.r;
          if (ddx * ddx + ddy * ddy < hitR * hitR) {
            en.hp -= b.dmg;
            particles.burst(b.x, b.y, 5, en.color, 160, 0.3);
            if (en.hp <= 0) {
              particles.burst(en.x, en.y, 16, en.color, 360, 0.7, Math.PI * 2, 0, 2, 5);
              SFX.boom();
              enemies.kill(en, false);
              demoEnemies.splice(i, 1);
            } else {
              SFX.hit();
            }
            if (!b.pierce) { bullets.kill(b); break; }
            b.pierceCt -= 1;
            if (b.pierceCt <= 0) { bullets.kill(b); break; }
          }
        }
      }

      // Demo weapon — fires forward only (aimed along velocity), and only when an asteroid
      // is in the forward cone within engagement range. Weapon's own fireTimer rate-limits.
      demoFireCD -= dt;
      if (demoFireCD < 0) demoFireCD = 0;
      const sp2 = Math.hypot(demoVx, demoVy);
      const fwdAng = sp2 > 30 ? Math.atan2(demoVy, demoVx) : demoShip.rotation;
      const fwdX = Math.cos(fwdAng), fwdY = Math.sin(fwdAng);
      let target: Enemy | null = null;
      let bestD = 380 * 380;
      for (const rk of [...demoRocks, ...demoEnemies]) {
        if (!rk.alive) continue;
        const ddx = rk.x - demoX, ddy = rk.y - demoY;
        const d = ddx * ddx + ddy * ddy;
        if (d < 1) continue;
        const len = Math.sqrt(d);
        const dot = (ddx / len) * fwdX + (ddy / len) * fwdY;
        if (dot < 0.7) continue;
        if (d < bestD) { bestD = d; target = rk; }
      }
      if (target && demoFireCD <= 0) {
        const tierBase = demoTier * 9;
        const tierSlot = Math.floor(Math.random() * 9);
        const wIdx = Math.min(WEAPONS.length - 1, tierBase + tierSlot);
        const aimAng = fwdAng; // strictly forward, not toward asteroid
        const fakePlayer = { x: demoX, y: demoY, aimAng } as any;
        const ft = { v: 0 };
        if (fire(wIdx, fakePlayer, bullets, ft, 1, 1)) {
          demoFireCD = WEAPONS[wIdx].rate;
          SFX.shootByWeapon(WEAPONS[wIdx].name);
        }
      }

      // Periodic nova bomb showcase — 8-15s CD, fires when ≥1 asteroid is in radius.
      // Title-mode is gentler than the in-game bomb: no shake, smaller flash.
      demoNovaCD -= dt;
      if (demoNovaCD <= 0 && demoRocks.length > 0) {
        const NOVA_R = 360;
        let cleared = 0;
        for (let i = demoRocks.length - 1; i >= 0; i--) {
          const rk = demoRocks[i];
          const ddx = rk.x - demoX, ddy = rk.y - demoY;
          if (ddx * ddx + ddy * ddy < NOVA_R * NOVA_R) {
            particles.burst(rk.x, rk.y, 14, 0xffaa00, 360, 0.6, Math.PI * 2, 0, 2, 5);
            particles.burst(rk.x, rk.y, 6, 0xffffff, 220, 0.4);
            enemies.kill(rk, false);
            demoRocks.splice(i, 1);
            cleared += 1;
          }
        }
        if (cleared > 0) {
          particles.burst(demoX, demoY, 80, 0xffaa00, 700, 1.1, Math.PI * 2, 0, 3, 8);
          particles.burst(demoX, demoY, 24, 0xffffff, 360, 0.5);
          triggerShockwave(demoX, demoY, 0.8, 32, 160);
          triggerFlash(0.30, 0xffaa44);
          SFX.boom();
          SFX.special();
          demoNovaCD = 8 + Math.random() * 7;
        } else {
          // Try again soon if no rocks were in range
          demoNovaCD = 1.5;
        }
      }
      if (demoGlow) {
        const tierColors = [0x00ffff, 0x44ddff, 0xff44ff, 0xffaa00, 0xff2200, 0xffffaa];
        const tierOuter  = [1.8, 2.0, 2.4, 2.8, 3.2, 3.8];
        demoGlow.color = tierColors[demoTier];
        demoGlow.outerStrength = tierOuter[demoTier];
      }

      // Free flight: ship can roam the entire viewport (over UI included)
      const minX = 60, maxX = G.W - 60;
      const minY = 60, maxY = G.H - 60;

      if (!demoInited) {
        demoX = (minX + maxX) / 2;
        demoY = (minY + maxY) / 2;
        demoTargetX = demoX; demoTargetY = demoY;
        demoInited = true;
      }

      // Pick a new target every 1.6-2.6s
      demoTargetCD -= dt;
      if (demoTargetCD <= 0) {
        demoTargetCD = 1.6 + Math.random() * 1.0;
        demoTargetX = minX + Math.random() * (maxX - minX);
        demoTargetY = minY + Math.random() * (maxY - minY);
      }

      // Player-physics constants (mirror player.ts: VEL_LERP=0.08*60, TARGET_PULL=3.0, MAX=700)
      const VEL_LERP = 0.08 * 60;
      const TARGET_PULL = 3.0;
      const MAX_SPEED = 700;

      // Cycle shield state every 5-8s — when on, ship is "fearless" and ignores lasers
      demoShieldCD -= dt;
      if (demoShieldCD <= 0) {
        const wasOn = demoShieldOn;
        demoShieldOn = !demoShieldOn;
        demoShieldCD = demoShieldOn ? 4.5 + Math.random() * 2.5 : 5.0 + Math.random() * 3.0;
        // Spawn a small "engage" particle burst on activation
        if (!wasOn && demoShieldOn) {
          particles.burst(demoX, demoY, 24, 0x4488ff, 280, 0.4, Math.PI * 2, 0, 1.4, 3);
        }
      }

      // Pull-target = the chosen waypoint, with laser-dodge (skipped if shielded) AND mouse-avoid
      let tx = demoTargetX, ty = demoTargetY;
      const SAFE_R = 200;
      const dodge = !demoShieldOn ? terrain.laserDodgeVector(demoX, demoY, SAFE_R, G.W, G.H) : null;
      if (dodge) {
        const urgency = 1 - dodge.dist / SAFE_R;
        tx = demoX + dodge.x * (160 + urgency * 240);
        ty = demoY + dodge.y * (160 + urgency * 240);
      }
      // Mouse avoidance — treat cursor as a soft threat
      const MOUSE_R = 240;
      const mdx = demoX - G.mouse.x, mdy = demoY - G.mouse.y;
      const mdist = Math.hypot(mdx, mdy);
      if (mdist < MOUSE_R) {
        const urgency = 1 - mdist / MOUSE_R;
        const mNorm = mdist > 0.001 ? mdist : 1;
        const ax = mdx / mNorm, ay = mdy / mNorm;
        // Blend by averaging with current target shift
        tx = (tx + (demoX + ax * (140 + urgency * 200))) * 0.5;
        ty = (ty + (demoY + ay * (140 + urgency * 200))) * 0.5;
      }

      // Wall avoidance — soft repulsion when within margin of any bound, so the
      // ship visibly steers away from edges instead of slamming into the clamp.
      // Demo mode has no death, but a hard wall-bounce reads as "ship hit wall".
      const WALL_MARGIN = 140;
      let wallPushX = 0, wallPushY = 0;
      const dL = demoX - minX, dR = maxX - demoX;
      const dT = demoY - minY, dB = maxY - demoY;
      if (dL < WALL_MARGIN) wallPushX += Math.pow(1 - dL / WALL_MARGIN, 2);
      if (dR < WALL_MARGIN) wallPushX -= Math.pow(1 - dR / WALL_MARGIN, 2);
      if (dT < WALL_MARGIN) wallPushY += Math.pow(1 - dT / WALL_MARGIN, 2);
      if (dB < WALL_MARGIN) wallPushY -= Math.pow(1 - dB / WALL_MARGIN, 2);
      if (wallPushX !== 0 || wallPushY !== 0) {
        // Pull target inward proportional to wall proximity (max 260px shove)
        tx = (tx + (demoX + wallPushX * 260)) * 0.5;
        ty = (ty + (demoY + wallPushY * 260)) * 0.5;
      }

      const k = VEL_LERP * dt;
      demoVx += ((tx - demoX) * TARGET_PULL - demoVx) * k;
      demoVy += ((ty - demoY) * TARGET_PULL - demoVy) * k;

      // Demo BH gravity — route through the real hazards system. The proxy is mutated
      // in place so demoVx/demoVy pick up both the radial pull and the tangential spiral
      // component (same physics in-game uses).
      {
        const proxy = { x: demoX, y: demoY, vx: demoVx, vy: demoVy };
        hazards.pullVelocity(proxy, dt, 0.3);
        demoVx = proxy.vx;
        demoVy = proxy.vy;
      }

      const sp = Math.hypot(demoVx, demoVy);
      if (sp > MAX_SPEED) { demoVx = (demoVx / sp) * MAX_SPEED; demoVy = (demoVy / sp) * MAX_SPEED; }

      demoX += demoVx * dt;
      demoY += demoVy * dt;
      // Safety clamp — should rarely fire now that wall-avoid steering is in.
      // Zero the component instead of bouncing so a clip never reads as a wall slam.
      if (demoX < minX) { demoX = minX; demoVx = 0; }
      if (demoX > maxX) { demoX = maxX; demoVx = 0; }
      if (demoY < minY) { demoY = minY; demoVy = 0; }
      if (demoY > maxY) { demoY = maxY; demoVy = 0; }

      // Hard-clamp out of any active laser (only when unshielded — shielded ship reflects)
      if (!demoShieldOn) {
        for (let it = 0; it < 4; it++) {
          const probe = terrain.laserDodgeVector(demoX, demoY, 22, G.W, G.H);
          if (!probe) break;
          const push = (22 - probe.dist) + 4;
          demoX += probe.x * push;
          demoY += probe.y * push;
        }
      } else {
        // Shielded: consume any laser the ship is currently inside (visual reflect)
        const las = terrain.consumeLaser({ x: demoX, y: demoY, radius: 18 }, G.W, G.H);
        if (las) {
          const refAng = las.angle + Math.PI;
          for (let i = 0; i < 5; i++) {
            const off = (i - 2) * 0.10;
            const a = refAng + off;
            bullets.spawn({
              x: demoX + Math.cos(a) * 18,
              y: demoY + Math.sin(a) * 18,
              vx: Math.cos(a) * 1100, vy: Math.sin(a) * 1100,
              dmg: 30, r: 5, color: 0xff44ff, pierce: true, pierceCt: 4,
            });
          }
          particles.burst(demoX, demoY, 18, 0x4488ff, 320, 0.4);
        }
      }

      const px = demoX, py = demoY;
      demoShip.x = px; demoShip.y = py;
      demoShip.alpha = 0.95 * introEased;
      // Rotation follows velocity tangent (only when moving)
      if (Math.hypot(demoVx, demoVy) > 30) {
        demoShip.rotation = Math.atan2(demoVy, demoVx);
      }
      // Trail — MeshRope ribbon (mid+core), tinted cyan, head=newest
      demoTrailPoints.unshift({ x: px, y: py });
      if (demoTrailPoints.length > DEMO_TRAIL_MAX) demoTrailPoints.pop();
      const dn = demoTrailPoints.length;
      const dRopeVisible = !screens.current && dn >= 2;
      demoTrailMid.visible = dRopeVisible;
      demoTrailCore.visible = dRopeVisible;
      if (dRopeVisible) {
        const dTail = demoTrailPoints[dn - 1];
        for (let i = 0; i < DEMO_TRAIL_MAX; i++) {
          const src = i < dn ? demoTrailPoints[i] : dTail;
          demoRopePoints[i].x = src.x;
          demoRopePoints[i].y = src.y;
        }
        demoTrailMid.alpha = 0.7 * introEased;
        demoTrailCore.alpha = 0.95 * introEased;
      }

      // P2 demo bot — mirrors P1 AI, constrained to right half of screen
      if (coop.enabled && !screens.current) {
        const minX2 = G.W * 0.35, maxX2 = G.W - 60;
        const minY2 = 60, maxY2 = G.H - 60;
        if (!demoInited2) {
          demoX2 = G.W * 0.72; demoY2 = G.H * 0.65;
          demoTargetX2 = demoX2; demoTargetY2 = demoY2;
          demoInited2 = true;
        }
        demoTargetCD2 -= dt;
        if (demoTargetCD2 <= 0) {
          demoTargetCD2 = 1.6 + Math.random() * 1.0;
          demoTargetX2 = minX2 + Math.random() * (maxX2 - minX2);
          demoTargetY2 = minY2 + Math.random() * (maxY2 - minY2);
        }
        demoShieldCD2 -= dt;
        if (demoShieldCD2 <= 0) {
          const wasOn2 = demoShieldOn2;
          demoShieldOn2 = !demoShieldOn2;
          demoShieldCD2 = demoShieldOn2 ? 4.5 + Math.random() * 2.5 : 5.0 + Math.random() * 3.0;
          if (!wasOn2 && demoShieldOn2) particles.burst(demoX2, demoY2, 24, 0xff44cc, 280, 0.4, Math.PI * 2, 0, 1.4, 3);
        }
        let tx2 = demoTargetX2, ty2 = demoTargetY2;
        const SAFE_R2 = 200;
        const dodge2 = !demoShieldOn2 ? terrain.laserDodgeVector(demoX2, demoY2, SAFE_R2, G.W, G.H) : null;
        if (dodge2) {
          const urg2 = 1 - dodge2.dist / SAFE_R2;
          tx2 = demoX2 + dodge2.x * (160 + urg2 * 240);
          ty2 = demoY2 + dodge2.y * (160 + urg2 * 240);
        }
        const mdx2 = demoX2 - G.mouse.x, mdy2 = demoY2 - G.mouse.y;
        const mdist2 = Math.hypot(mdx2, mdy2);
        if (mdist2 < 240) {
          const urg2 = 1 - mdist2 / 240;
          const mn2 = mdist2 > 0.001 ? mdist2 : 1;
          tx2 = (tx2 + (demoX2 + mdx2 / mn2 * (140 + urg2 * 200))) * 0.5;
          ty2 = (ty2 + (demoY2 + mdy2 / mn2 * (140 + urg2 * 200))) * 0.5;
        }
        const WM2 = 140;
        let wpx2 = 0, wpy2 = 0;
        const dL2 = demoX2 - minX2, dR2 = maxX2 - demoX2, dT2 = demoY2 - minY2, dB2 = maxY2 - demoY2;
        if (dL2 < WM2) wpx2 += Math.pow(1 - dL2 / WM2, 2);
        if (dR2 < WM2) wpx2 -= Math.pow(1 - dR2 / WM2, 2);
        if (dT2 < WM2) wpy2 += Math.pow(1 - dT2 / WM2, 2);
        if (dB2 < WM2) wpy2 -= Math.pow(1 - dB2 / WM2, 2);
        if (wpx2 !== 0 || wpy2 !== 0) { tx2 = (tx2 + (demoX2 + wpx2 * 260)) * 0.5; ty2 = (ty2 + (demoY2 + wpy2 * 260)) * 0.5; }
        const k2 = 0.08 * 60 * dt;
        demoVx2 += ((tx2 - demoX2) * 3.0 - demoVx2) * k2;
        demoVy2 += ((ty2 - demoY2) * 3.0 - demoVy2) * k2;
        { const proxy2 = { x: demoX2, y: demoY2, vx: demoVx2, vy: demoVy2 };
          hazards.pullVelocity(proxy2, dt, 0.3); demoVx2 = proxy2.vx; demoVy2 = proxy2.vy; }
        const sp2b = Math.hypot(demoVx2, demoVy2);
        if (sp2b > 700) { demoVx2 = demoVx2 / sp2b * 700; demoVy2 = demoVy2 / sp2b * 700; }
        demoX2 += demoVx2 * dt; demoY2 += demoVy2 * dt;
        if (demoX2 < minX2) { demoX2 = minX2; demoVx2 = 0; }
        if (demoX2 > maxX2) { demoX2 = maxX2; demoVx2 = 0; }
        if (demoY2 < minY2) { demoY2 = minY2; demoVy2 = 0; }
        if (demoY2 > maxY2) { demoY2 = maxY2; demoVy2 = 0; }
        if (!demoShieldOn2) {
          for (let it = 0; it < 4; it++) {
            const probe2 = terrain.laserDodgeVector(demoX2, demoY2, 22, G.W, G.H);
            if (!probe2) break;
            demoX2 += probe2.x * ((22 - probe2.dist) + 4);
            demoY2 += probe2.y * ((22 - probe2.dist) + 4);
          }
        } else {
          const las2 = terrain.consumeLaser({ x: demoX2, y: demoY2, radius: 18 }, G.W, G.H);
          if (las2) {
            const refAng2 = las2.angle + Math.PI;
            for (let i = 0; i < 5; i++) {
              const a = refAng2 + (i - 2) * 0.10;
              bullets.spawn({ x: demoX2 + Math.cos(a) * 18, y: demoY2 + Math.sin(a) * 18, vx: Math.cos(a) * 1100, vy: Math.sin(a) * 1100, dmg: 30, r: 5, color: 0xff44cc, pierce: true, pierceCt: 4 });
            }
            particles.burst(demoX2, demoY2, 18, 0xff44cc, 320, 0.4);
          }
        }
        // Fire — same weapon system as P1
        demoFireCD2 -= dt;
        if (demoFireCD2 < 0) demoFireCD2 = 0;
        const fwdAng2 = sp2b > 30 ? Math.atan2(demoVy2, demoVx2) : demoShip2.rotation;
        const fwdX2 = Math.cos(fwdAng2), fwdY2 = Math.sin(fwdAng2);
        let target2: Enemy | null = null, bestD2 = 380 * 380;
        for (const rk of [...demoRocks, ...demoEnemies]) {
          if (!rk.alive) continue;
          const ddx = rk.x - demoX2, ddy = rk.y - demoY2;
          const d = ddx * ddx + ddy * ddy;
          if (d < 1) continue;
          const len = Math.sqrt(d);
          if ((ddx / len) * fwdX2 + (ddy / len) * fwdY2 < 0.7) continue;
          if (d < bestD2) { bestD2 = d; target2 = rk; }
        }
        if (target2 && demoFireCD2 <= 0) {
          const wIdx2 = Math.min(WEAPONS.length - 1, demoTier * 9 + Math.floor(Math.random() * 9));
          const fakePlayer2 = { x: demoX2, y: demoY2, aimAng: fwdAng2 } as any;
          const ft2 = { v: 0 };
          if (fire(wIdx2, fakePlayer2, bullets, ft2, 1, 1)) {
            demoFireCD2 = WEAPONS[wIdx2].rate;
            SFX.shootByWeapon(WEAPONS[wIdx2].name);
          }
        }
        // Nova bomb
        demoNovaCD2 -= dt;
        if (demoNovaCD2 <= 0 && demoRocks.length > 0) {
          let cleared2 = 0;
          for (let i = demoRocks.length - 1; i >= 0; i--) {
            const rk = demoRocks[i];
            const ddx = rk.x - demoX2, ddy = rk.y - demoY2;
            if (ddx * ddx + ddy * ddy < 360 * 360) {
              particles.burst(rk.x, rk.y, 14, 0xff44cc, 360, 0.6, Math.PI * 2, 0, 2, 5);
              particles.burst(rk.x, rk.y, 6, 0xffffff, 220, 0.4);
              enemies.kill(rk, false); demoRocks.splice(i, 1); cleared2++;
            }
          }
          if (cleared2 > 0) {
            particles.burst(demoX2, demoY2, 80, 0xff44cc, 700, 1.1, Math.PI * 2, 0, 3, 8);
            particles.burst(demoX2, demoY2, 24, 0xffffff, 360, 0.5);
            triggerShockwave(demoX2, demoY2, 0.8, 32, 160);
            triggerFlash(0.30, 0xff44cc);
            SFX.boom(); SFX.special();
            demoNovaCD2 = 8 + Math.random() * 7;
          } else { demoNovaCD2 = 1.5; }
        }
        // Render
        demoShip2.visible = true;
        demoShip2.x = demoX2; demoShip2.y = demoY2;
        demoShip2.alpha = 0.90 * introEased;
        if (sp2b > 30) demoShip2.rotation = Math.atan2(demoVy2, demoVx2);
        demoTrailPoints2.unshift({ x: demoX2, y: demoY2 });
        if (demoTrailPoints2.length > DEMO_TRAIL_MAX2) demoTrailPoints2.pop();
        const dn2 = demoTrailPoints2.length;
        const d2Visible = dn2 >= 2;
        demoTrailMid2.visible = d2Visible; demoTrailCore2.visible = d2Visible;
        if (d2Visible) {
          const tail2 = demoTrailPoints2[dn2 - 1];
          for (let i = 0; i < DEMO_TRAIL_MAX2; i++) {
            const s2 = i < dn2 ? demoTrailPoints2[i] : tail2;
            demoRopePoints2[i].x = s2.x; demoRopePoints2[i].y = s2.y;
          }
          demoTrailMid2.alpha = 0.7 * introEased;
          demoTrailCore2.alpha = 0.90 * introEased;
        }
      } else {
        demoShip2.visible = false;
        demoTrailMid2.visible = false; demoTrailCore2.visible = false;
        if (!coop.enabled) { demoTrailPoints2.length = 0; demoInited2 = false; }
      }
      return;
    }

    // Hero overlay for victory / paused / gameover — local closures bind Text refs to module helpers
    const setHeroTitle = (text: string, color: number, glowColor: number): void => applyHeroTitle(overlayTitle, text, G.W, color, glowColor);
    const setHeroSub = (text: string, color: number): void => applyHeroSub(overlaySub, text, G.W, color);

    if (G.state === 'victory') {
      overlay.visible = true;
      overlayBg.clear();
      overlayBg.rect(0, 0, G.W, G.H);
      overlayBg.fill({ color: 0x001020, alpha: 0.85 });
      // Decorative center glow gradient (broad)
      overlayBg.rect(0, G.H * 0.28, G.W, 4);
      overlayBg.fill({ color: 0x00ffaa, alpha: 0.5 });
      setHeroTitle('VICTOIRE', 0xffffff, 0x00ffaa);
      overlayTitle.y = G.H * 0.30;
      const tag = lbHighlight.idx >= 0 ? `  ·  ★ NOUVEAU MEILLEUR SCORE ★` : '';
      setHeroSub(`25 VAGUES TERMINÉES  ·  SCORE ${hud.score}${tag}  ·  R POUR REJOUER`, 0xaaeeff);
      overlaySub.y = G.H * 0.40;
      const lb = getLB();
      const lines = ['─── CLASSEMENT CAMPAGNE ───', ...lb.slice(0, 5).map((r, i) => {
        const mark = i === lbHighlight.idx && lbHighlight.t > 0 ? '►' : ' ';
        return `${mark}${(i + 1).toString().padStart(2)}. ${r.score.toString().padStart(8)}  ${r.date}`;
      })];
      lbText.text = lines.join('\n');
      lbText.anchor.set(0.5, 0);
      lbText.style.fill = 0xffdd00;
      lbText.style.fontSize = 14;
      lbText.style.letterSpacing = 1.5;
      lbText.x = G.W / 2; lbText.y = G.H * 0.50;
      if (lbHighlight.t > 0) lbHighlight.t -= dt;
      return;
    }

    if (G.state === 'paused') {
      overlay.visible = true;
      overlayBg.clear();
      overlayBg.rect(0, 0, G.W, G.H);
      overlayBg.fill({ color: 0x02040c, alpha: 0.75 });
      overlayBg.rect(0, G.H * 0.48, G.W, 3);
      overlayBg.fill({ color: 0x00ffff, alpha: 0.55 });
      setHeroTitle('EN PAUSE', 0xffffff, 0x00ffff);
      overlayTitle.y = G.H * 0.46;
      setHeroSub(`ÉCHAP POUR REPRENDRE  ·  ${keyLabel('KeyR')} POUR RECOMMENCER  ·  ${keyLabel('KeyQ')} POUR MENU`, 0x88aacc);
      overlaySub.y = G.H * 0.56;
      lbText.text = '';
      return;
    }

    if (G.state === 'gameover') {
      overlay.visible = true;
      overlayBg.clear();
      overlayBg.rect(0, 0, G.W, G.H);
      overlayBg.fill({ color: 0x180000, alpha: 0.82 });
      overlayBg.rect(0, G.H * 0.30, G.W, 3);
      overlayBg.fill({ color: 0xff4444, alpha: 0.55 });
      setHeroTitle('PARTIE TERMINÉE', 0xffffff, 0xff4444);
      overlayTitle.y = G.H * 0.32;
      const tag = lbHighlight.idx >= 0 ? `  ·  ★ NOUVEAU MEILLEUR SCORE ★` : '';
      setHeroSub(`SCORE ${hud.score}   ·   VAGUE ${wave.wave}${tag}   ·   R POUR RÉESSAYER`, 0xffaaaa);
      overlaySub.y = G.H * 0.42;
      const lb = getLB();
      const lines = ['─── CLASSEMENT ───', ...lb.slice(0, 5).map((r, i) => {
        const mark = i === lbHighlight.idx && lbHighlight.t > 0 ? '►' : ' ';
        return `${mark}${(i + 1).toString().padStart(2)}. ${r.score.toString().padStart(8)}  ${r.date}`;
      })];
      lbText.text = lines.join('\n');
      lbText.anchor.set(0.5, 0);
      lbText.style.fill = lbHighlight.idx >= 0 && lbHighlight.t > 0 ? 0xffdd00 : 0xffcc00;
      lbText.style.fontSize = 14;
      lbText.style.letterSpacing = 1.5;
      lbText.x = G.W / 2; lbText.y = G.H * 0.50;
      if (lbHighlight.t > 0) lbHighlight.t -= dt;
      return;
    }

    overlay.visible = false;

    if (respawnT > 0) {
      // Hide the ship + trail completely while waiting to respawn — explosion already
      // played; nothing should sit there. setVisible covers view + 3 mesh ropes.
      player.setVisible(false);
      respawnT -= dt;
      if (respawnT <= 0) {
        player.setVisible(true);
        player.reset(G.W * 0.25, G.H / 2);
      }
    }

    if (player2 && !player2.dead) {
      player2.firing = keys.fire;
      player2.update(dt);
      if (player2.firing && player2.fireTimer <= 0) {
        player2.fireTimer = 0.16;
        bullets.spawn({
          x: player2.x + Math.cos(player2.aimAng) * 14,
          y: player2.y + Math.sin(player2.aimAng) * 14,
          vx: Math.cos(player2.aimAng) * 1000,
          vy: Math.sin(player2.aimAng) * 1000,
          dmg: 8, r: 4, color: 0xff44cc,
        });
        SFX.shoot();
      }
    }

    if (respawnT <= 0) {
      player.update(dt);
      // Engine exhaust particles (opposite direction of aim)
      const exA = player.aimAng + Math.PI;
      const ex = player.x + Math.cos(exA) * 14;
      const ey = player.y + Math.sin(exA) * 14;
      particles.burst(ex, ey, 2, 0xffaa00, 180, 0.18, 0.4, exA, 1, 3);
      particles.burst(ex, ey, 1, 0xffffff, 120, 0.12, 0.25, exA, 1, 2);
      if (terrain.collideSpike(player, G.W, G.H) && player.invuln <= 0) {
        if (player.shield > 0) {
          // Reflect: shield consumes the beam — spawn deflection bullets back along the laser direction
          const las = terrain.consumeLaser(player, G.W, G.H);
          player.shield = Math.max(0, player.shield - 2);
          player.invuln = 14;
          if (las) {
            const refAng = las.angle + Math.PI; // back toward source
            for (let i = 0; i < 5; i++) {
              const off = (i - 2) * 0.10;
              const a = refAng + off;
              bullets.spawn({
                x: player.x + Math.cos(a) * 18,
                y: player.y + Math.sin(a) * 18,
                vx: Math.cos(a) * 1100, vy: Math.sin(a) * 1100,
                dmg: 30, r: 5, color: 0xff44ff, pierce: true, pierceCt: 4,
              });
            }
            particles.burst(player.x, player.y, 24, 0x4488ff, 360, 0.45, Math.PI * 1.5, refAng, 1.2, 3);
            triggerFlash(0.35, 0x4488ff);
            shake(8, 0.18);
          }
          SFX.hit();
        } else {
          const dmg = 14;
          if (!settings.godMode) { player.hp -= dmg; wave.stats.dmgTaken += dmg; }
          player.invuln = 35;
          particles.burst(player.x, player.y, 10, 0xff2200, 280, 0.5);
          SFX.hit();
          if (player.hp <= 0) onPlayerDeath();
        }
      }
      if (terrain.collidePlayer(player, G.W, G.H) && player.invuln <= 0) {
        const dmg = 12;
        if (player.shield > 0) {
          player.shield = Math.max(0, player.shield - dmg);
          player.invuln = 25;
        } else {
          if (!settings.godMode) { player.hp -= dmg; wave.stats.dmgTaken += dmg; }
          player.invuln = 40;
        }
        particles.burst(player.x, player.y, 8, 0xff8844, 240, 0.4);
        SFX.hit();
        if (player.hp <= 0) onPlayerDeath();
      }
      if (player2 && !player2.dead && terrain.collidePlayer(player2, G.W, G.H) && player2.invuln <= 0) {
        const dmg = 12;
        if (player2.shield > 0) { player2.shield = Math.max(0, player2.shield - dmg); player2.invuln = 25; }
        else { player2.hp -= dmg; player2.invuln = 40; }
        particles.burst(player2.x, player2.y, 8, 0xff8844, 240, 0.4);
        SFX.hit();
        if (player2.hp <= 0) { player2.dead = true; player2.view.visible = false; lives -= 1; if (lives < 0) G.state = 'gameover'; }
      }
      if (fireTimer.v > 0) fireTimer.v -= dt;
      if (charge.t > 0) {
        charge.t += dt;
        if (charge.t >= 0.5 && !charge.released) charge.released = 1;
      }
      if (G.mouse.down && charge.t <= 0) {
        const dMul = (fx.synergy > 0 ? 1.5 : 1) * dmgMul;
        if (fire(weaponIdx, player, bullets, fireTimer, dMul, fx.synergy > 0 ? 0.5 : 1)) {
          SFX.shootByWeapon(WEAPONS[weaponIdx].name);
          wave.stats.shotsFired += 1;
        }
      }
    }
    if (bashCooldown > 0) bashCooldown = Math.max(0, bashCooldown - dt);
    if (fx.slowField > 0) fx.slowField = Math.max(0, fx.slowField - dt);
    if (fx.shieldSurge > 0) fx.shieldSurge = Math.max(0, fx.shieldSurge - dt);

    // Addon system: missile + ring bursts at weapon tiers
    {
      const r = weaponAddons.tick(dt, weaponIdx, respawnT <= 0, player, enemies.enemies, bullets);
      missileTier = r.missileTier;
      ringActive = r.ringActive;
    }

    // Slow-field global tint — yellow vignette + edge glow
    drawSlowFieldEdges(slowOverlay, fx.slowField, G.W, G.H);

    // Black-hole proximity warp — chromatic-aberration ring + vignette around player
    // (or demo ship while on the title screen).
    bhWarpOverlay.clear();
    const drawBhWarp = (sx: number, sy: number, dx: number, dy: number, intensity: number): void => {
      if (intensity < 0.05) return;
      const k = intensity;
      const ox = dx * 2 * k;
      const oy = dy * 2 * k;
      const baseR = 42;
      bhWarpOverlay.circle(sx + ox, sy + oy, baseR);
      bhWarpOverlay.stroke({ width: 2, color: 0x00ffff, alpha: 0.35 * k });
      bhWarpOverlay.circle(sx - ox, sy - oy, baseR);
      bhWarpOverlay.stroke({ width: 2, color: 0xff44ff, alpha: 0.35 * k });
      bhWarpOverlay.circle(sx, sy, 22);
      bhWarpOverlay.stroke({ width: 1.5, color: 0xffffff, alpha: 0.25 * k });
      bhWarpOverlay.blendMode = 'normal';
      bhWarpOverlay.circle(sx + dx * 80, sy + dy * 80, 110);
      bhWarpOverlay.fill({ color: 0x000010, alpha: 0.28 * k });
      bhWarpOverlay.blendMode = 'add';
    };
    if (G.state === 'playing' && respawnT <= 0) {
      const inf = hazards.nearestBhInfluence(player.x, player.y);
      if (inf) drawBhWarp(player.x, player.y, inf.dx, inf.dy, inf.intensity);
    } else if ((G.state as string) === 'title' && demoShip.visible) {
      // Same hazards.nearestBhInfluence helper as in-game — single source of truth
      const inf = hazards.nearestBhInfluence(demoX, demoY);
      if (inf) drawBhWarp(demoX, demoY, inf.dx, inf.dy, inf.intensity);
    }

    // Player HP + shield indicator rings around the ship — outer arc HP, inner arc shield
    playerShieldRing.clear();
    if (G.state === 'playing' && respawnT <= 0) {
      const hpFrac = Math.max(0, Math.min(1, player.hp / player.maxHp));
      const shFrac = Math.max(0, Math.min(1, player.shield / player.maxShield));
      drawStatusRings(playerShieldRing, player.x, player.y, hpFrac, shFrac, 0);
    }
    if (G.state === 'playing' && coop.enabled && player2 && !player2.dead) {
      const hpFrac = Math.max(0, Math.min(1, player2.hp / player2.maxHp));
      const shFrac = Math.max(0, Math.min(1, player2.shield / player2.maxShield));
      drawStatusRings(playerShieldRing, player2.x, player2.y, hpFrac, shFrac, 1);
    }

    // Player auras — synergy magenta ring + shield-surge cyan ring
    drawSynergyAura(synergyRing, player.x, player.y, fx.synergy, respawnT <= 0);
    drawShieldSurge(surgeRing, player.x, player.y, fx.shieldSurge, respawnT <= 0);

    // shield surge aura: 3 hp/frame contact damage to nearby enemies
    if (fx.shieldSurge > 0 && respawnT <= 0) {
      for (const en of enemies.enemies) {
        if (!en.alive) continue;
        if (en.kind === 'boss' || en.kind === 'miniboss') continue;
        const dx = en.x - player.x, dy = en.y - player.y;
        if (dx * dx + dy * dy < 90 * 90) {
          en.hp -= 3;
          en.hitFlashT = 4;
          if (en.hp <= 0) {
            hud.score += en.val;
            const drop = rollPowerup(en.kind);
            if (drop) powerups.spawn(drop, en.x, en.y);
            enemies.kill(en, true);
            combo += 1; comboTimer = 2.6; hud.combo = combo;
          }
        }
      }
    }

    hazards.update(dt, G.W, G.H, wave.wave % 5 === 0, wave.waveBreak);

    // Comet spawner — non-boss waves only, 6..10s cadence
    if (G.state === 'playing' && !wave.waveBreak && wave.wave % 5 !== 0) {
      cometT += dt;
      if (cometT >= cometNext) {
        cometT = 0;
        cometNext = 6 + Math.random() * 4;
        enemies.spawn('comet', 1);
      }
    }

    // Apply gravity pulls + consumption
    if (respawnT <= 0) {
      hazards.pullVelocity(player, dt, 0.3, true);
      if (hazards.consumes(player)) {
        // Player is just pulled, never killed by hazard core
      }
      if (hazards.warp(player, 'p1')) {
        // Lock the random exit direction for ~0.6s so it actually carries the ship
        // out — otherwise the mouse-pull lerp instantly overrides the random angle.
        player.warpMomentumT = 1.0;
      }
    }
    if (player2 && !player2.dead) {
      hazards.pullVelocity(player2, dt, 0.3, true);
      if (hazards.consumes(player2)) {
        player2.dead = true; player2.view.visible = false;
        SFX.death();
        particles.burst(player2.x, player2.y, 70, 0xff44cc, 480, 1.1, Math.PI * 2, 0, 2.6, 7);
        lives -= 1; if (lives < 0) G.state = 'gameover';
      }
      hazards.warp(player2, 'p2');
    }
    for (const b of bullets.pool) {
      if (!b.alive) continue;
      hazards.pullVelocity(b, dt, 1);
      if (hazards.consumes(b)) bullets.kill(b);
    }
    for (const eb of ebullets.pool) {
      if (!eb.alive) continue;
      hazards.pullVelocity(eb, dt, 1);
      if (hazards.consumes(eb)) ebullets.kill(eb);
    }
    for (const en of enemies.enemies) {
      if (!en.alive) continue;
      if (en.kind === 'boss' || en.kind === 'miniboss') continue;
      hazards.pullVelocity(en, dt, 0.4);
      if (hazards.consumes(en)) {
        hud.score += en.val * 2;
        particles.burst(en.x, en.y, 18, en.color, 320, 0.6);
        enemies.kill(en, false);
      }
    }

    const slowMul = (fx.slowField > 0 ? 0.22 : 1) * hazards.bhTimeScale(player.x, player.y);
    bullets.update(dt, G.W, G.H);
    enemies.update(dt * slowMul, G.W, G.H, { x: player.x, y: player.y });
    // F7 MIRROR — sync mirrored boss twin position + flicker each frame
    syncMirrorBoss(enemies.enemies, wave.bossArena === 'MIRROR' && wave.wave % 5 === 0, G.W);
    ebullets.update(dt * slowMul, G.W, G.H, { x: player.x, y: player.y });
    powerups.update(dt, G.W, G.H, respawnT <= 0 ? { x: player.x, y: player.y, r: 60 * magnetMul } : undefined);

    // enemy firing
    if (respawnT <= 0) {
      const mirrorFire = wave.bossArena === 'MIRROR' && wave.wave % 5 === 0;
      for (const e of enemies.enemies) {
        if (!e.alive) continue;
        if (e.fr > 0 && e.x < G.W) {
          const mx = mirrorFire && e.kind === 'boss' ? G.W - e.x : undefined;
          enemyFire(e, ebullets, { x: player.x, y: player.y }, mx);
        }
      }
    }

    // bullet ↔ enemy collisions
    const mirrorActive = wave.bossArena === 'MIRROR' && wave.wave % 5 === 0;
    for (const b of bullets.pool) {
      if (!b.alive) continue;
      for (const e of enemies.enemies) {
        if (!e.alive) continue;
        const dx = b.x - e.x, dy = b.y - e.y;
        const rr = (e.radius + b.r) * (e.radius + b.r);
        let hit = dx * dx + dy * dy < rr;
        if (!hit && mirrorActive && e.kind === 'boss') {
          const dxm = b.x - (G.W - e.x);
          if (dxm * dxm + dy * dy < rr) hit = true;
        }
        if (hit) {
          // Boss multi-part: bullet hits the closest alive part if any are within range;
          // core damage is heavily reduced while any part is alive.
          let dmg = b.dmg;
          let coreImpact = true;
          if (e.kind === 'boss') {
            const parts = (e as any).parts as Array<{ alive: boolean; hp: number; maxHp: number; r: number; x: number; y: number }> | undefined;
            if (parts && parts.length > 0) {
              let bestPart: typeof parts[number] | null = null;
              let bestD2 = Infinity;
              for (const p of parts) {
                if (!p.alive) continue;
                const px = b.x - p.x, py = b.y - p.y;
                const d2 = px * px + py * py;
                const rr = (p.r + b.r) * (p.r + b.r);
                if (d2 < rr && d2 < bestD2) { bestPart = p; bestD2 = d2; }
              }
              if (bestPart) {
                bestPart.hp -= dmg;
                coreImpact = false;
                if (bestPart.hp <= 0) {
                  bestPart.alive = false;
                  particles.burst(bestPart.x, bestPart.y, 24, 0x00ffcc, 480, 0.7, Math.PI * 2, 0, 2.2, 6);
                  particles.burst(bestPart.x, bestPart.y, 16, 0xffaa00, 380, 0.6);
                  particles.burst(bestPart.x, bestPart.y, 8, 0xffffff, 280, 0.4);
                  shake(10, 0.25);
                  SFX.boom();
                  // Last part down? Telegraph the core opening up
                  const stillAlive = parts.filter((p) => p.alive).length;
                  if (stillAlive === 0) {
                    triggerFlash(0.45, 0xff44ff);
                    triggerShockwave(e.x, e.y, 0.9, 28, 200);
                    wave.banner = { text: 'CŒUR EXPOSÉ', color: 0xff44ff, t: 110 };
                  }
                }
              } else {
                // No part hit — core takes only 5% damage while any part alive
                const aliveCount = parts.filter((p) => p.alive).length;
                if (aliveCount > 0) dmg = Math.max(0, Math.floor(dmg * 0.05));
              }
            }
          }
          if (coreImpact) {
            if (e.eShield > 0) {
              const absorbed = Math.min(e.eShield, dmg);
              e.eShield -= absorbed;
              const remainder = dmg - absorbed;
              if (remainder > 0) e.hp -= remainder;
            } else {
              e.hp -= dmg;
            }
          }
          e.hitFlashT = 6;
          // Velocity-aligned spark cone: forward bounce-back from impact direction
          const bulletAng = Math.atan2(b.vy, b.vx);
          const sparkAng = bulletAng + Math.PI; // bounce back toward shooter
          particles.burst(b.x, b.y, 5, e.color, 280, 0.3, Math.PI / 2.4, sparkAng, 1, 2.4);
          particles.burst(b.x, b.y, 2, 0xffffff, 320, 0.18, Math.PI / 3.5, sparkAng, 0.6, 1.4);
          // Tiny direction-aligned camera kick on bigger hits
          if (e.kind === 'boss' || e.kind === 'miniboss' || b.dmg >= 30) {
            shake(Math.min(8, 2 + b.dmg * 0.05), 0.18);
          }
          SFX.hit();
          wave.stats.shotsHit += 1;
          if (e.hp <= 0 && e.kind === 'boss' && !G.endless && wave.wave >= 25) {
            // CAMPAIGN VICTORY
            hud.score += e.val;
            particles.burst(e.x, e.y, 80, 0x00ffaa, 800, 1.4, Math.PI * 2, 0, 3, 8);
            SFX.boom();
            enemies.kill(e, true);
            if (qualifiesForLeaderboard(hud.score)) {
              const { idx } = addToLB(hud.score);
              lbHighlight.idx = idx;
              lbHighlight.t = 3;
            }
            G.state = 'victory';
            break;
          }
          if (e.hp <= 0) {
            const mul = (combo > 5 ? 1 + Math.min(0.6, combo * 0.02) : 1) * scoreMul;
            const gain = Math.floor(e.val * mul);
            hud.score += gain;
            if (combo > 5) floats.spawn(`+${gain}`, e.x, e.y, combo > 25 ? 0xffffff : combo > 12 ? 0xff2200 : 0xff8800, 14);
            combo += 1;
            comboTimer = 2.6;
            hud.combo = combo;
            hud.comboPulse = 18;
            wave.stats.kills += 1;
            if (combo > wave.stats.bestCombo) wave.stats.bestCombo = combo;
            if (e.kind !== 'asteroid' && e.kind !== 'boss' && e.kind !== 'miniboss') {
              waveKills += 1;
              for (const m of STREAK_MARKS) {
                if (waveKills === m && !streakHit[m]) {
                  streakHit[m] = true;
                  const r = Math.random();
                  if (r < 0.33) {
                    player.shield = Math.min(player.maxShield, player.shield + 4);
                    wave.banner = { text: `SÉRIE ×${m} +4 BOUCLIER`, color: 0x44aaff, t: 100 };
                  } else if (r < 0.66) {
                    if (weaponIdx < WEAPONS.length - 1) weaponIdx += 1;
                    wave.banner = { text: `SÉRIE ×${m} +1 ARME`, color: 0xff44ff, t: 100 };
                  } else {
                    const bonus = m * 150;
                    hud.score += bonus;
                    wave.banner = { text: `SÉRIE ×${m} +${bonus}`, color: 0xffaa00, t: 100 };
                  }
                }
              }
            }
            fireAch({ kind: 'kill' });
            fireAch({ kind: 'combo', n: combo });
            if (e.kind === 'asteroid') {
              runStats.asteroidsKilled += 1;
              fireAch({ kind: 'asteroid_kill', n: runStats.asteroidsKilled });
            }
            if (e.kind === 'boss') {
              runStats.bossKills += 1;
              fireAch({ kind: 'boss_kill' });
            }
            particles.burst(e.x, e.y, 20, e.color, 320, 0.7, Math.PI * 2, 0, 2, 4);
            if (e.elite) particles.burst(e.x, e.y, 18, 0xffcc00, 380, 0.9, Math.PI * 2, 0, 2, 5);
            if (e.kind === 'boss' && wave.bossArena === 'MIRROR' && wave.wave % 5 === 0) {
              particles.burst(G.W - e.x, e.y, 20, e.color, 320, 0.7, Math.PI * 2, 0, 2, 4);
            }
            if (e.kind === 'boss') {
              triggerShockwave(e.x, e.y, 1.4, 50, 220);
              triggerFlash(0.7, 0xffffff);
              shake(28, 0.7);
              particles.burst(e.x, e.y, 60, e.color, 700, 1.6, Math.PI * 2, 0, 3, 12);
              particles.burst(e.x, e.y, 30, 0xffffff, 480, 0.9, Math.PI * 2, 0, 2, 6);
            } else if (e.kind === 'miniboss') {
              triggerShockwave(e.x, e.y, 0.9, 28, 160);
              triggerFlash(0.4, 0xffaa44);
              shake(14, 0.4);
              particles.burst(e.x, e.y, 30, e.color, 460, 1.2, Math.PI * 2, 0, 2, 8);
            }
            SFX.boom();
            const drop = e.elite ? 'weapon' : rollPowerup(e.kind);
            if (drop) {
              const isGold = drop === 'weapon' && Math.random() < 0.01;
              powerups.spawn(drop, e.x, e.y, isGold);
            }
            // Asteroid splitting
            if (e.kind === 'asteroid') {
              const sz = (e as any).sz || 2;
              if (sz > 1) {
                for (let k = 0; k < 2; k++) {
                  const a = Math.random() * Math.PI * 2;
                  const sp = 100 + Math.random() * 80;
                  enemies.spawnAsteroidAt(e.x, e.y, sz - 1, Math.cos(a) * sp, Math.sin(a) * sp);
                }
              }
            }
            enemies.kill(e, true);
          }
          if (b.pierce) {
            b.pierceCt -= 1;
            if (b.pierceCt <= 0) bullets.kill(b);
          } else {
            bullets.kill(b);
          }
          break;
        }
      }
    }

    // P2 collisions
    if (player2 && !player2.dead && player2.invuln <= 0) {
      for (const en of enemies.enemies) {
        if (!en.alive) continue;
        const dx = player2.x - en.x, dy = player2.y - en.y;
        if (dx * dx + dy * dy < (en.radius + player2.radius) ** 2) {
          const dmg = en.kind === 'tank' ? 18 : en.kind === 'boss' ? 25 : 10;
          if (player2.shield > 0) { player2.shield = Math.max(0, player2.shield - dmg); player2.invuln = 20; }
          else { player2.hp -= dmg; player2.invuln = 40; }
          if (player2.hp <= 0) {
            player2.dead = true;
            player2.view.visible = false;
            // P2 death FX — same multi-layer explosion as P1 (white core + amber + magenta debris + red ember)
            SFX.death();
            particles.burst(player2.x, player2.y, 80, 0xffffff, 720, 0.40, Math.PI * 2, 0, 4.0, 9);
            particles.burst(player2.x, player2.y, 110, 0xffaa00, 600, 0.95, Math.PI * 2, 0, 3.0, 8);
            particles.burst(player2.x, player2.y, 70, 0xff44cc, 480, 1.10, Math.PI * 2, 0, 2.6, 7);
            particles.burst(player2.x, player2.y, 50, 0xff4422, 380, 1.45, Math.PI * 2, 0, 2.2, 6);
            triggerShockwave(player2.x, player2.y, 1.0, 32, 200);
            triggerFlash(0.65, 0xff44aa);
            shake(28, 0.7);
            lives -= 1;
            if (lives < 0) {
              const earned = calcShardsEarned(hud.score, runStats.bossKills, runStats.wavesCleared);
              if (earned > 0) addShards(earned);
              G.state = 'gameover';
            }
          }
          break;
        }
      }
      for (const eb of ebullets.pool) {
        if (!eb.alive) continue;
        const dx = eb.x - player2.x, dy = eb.y - player2.y;
        if (dx * dx + dy * dy < (eb.r + player2.radius) ** 2) {
          if (player2.shield > 0) { player2.shield = Math.max(0, player2.shield - eb.dmg); player2.invuln = 18; }
          else { player2.hp -= eb.dmg; player2.invuln = 36; }
          ebullets.kill(eb);
          if (player2.hp <= 0) {
            player2.dead = true;
            player2.view.visible = false;
            // P2 death FX — same multi-layer explosion as P1 (white core + amber + magenta debris + red ember)
            SFX.death();
            particles.burst(player2.x, player2.y, 80, 0xffffff, 720, 0.40, Math.PI * 2, 0, 4.0, 9);
            particles.burst(player2.x, player2.y, 110, 0xffaa00, 600, 0.95, Math.PI * 2, 0, 3.0, 8);
            particles.burst(player2.x, player2.y, 70, 0xff44cc, 480, 1.10, Math.PI * 2, 0, 2.6, 7);
            particles.burst(player2.x, player2.y, 50, 0xff4422, 380, 1.45, Math.PI * 2, 0, 2.2, 6);
            triggerShockwave(player2.x, player2.y, 1.0, 32, 200);
            triggerFlash(0.65, 0xff44aa);
            shake(28, 0.7);
            lives -= 1;
            if (lives < 0) {
              G.state = 'gameover';
            }
          }
        }
      }
    }

    // ebullet ↔ player
    if (respawnT <= 0 && player.invuln <= 0) {
      for (const eb of ebullets.pool) {
        if (!eb.alive) continue;
        const dx = eb.x - player.x, dy = eb.y - player.y;
        if (dx * dx + dy * dy < (eb.r + player.radius) * (eb.r + player.radius)) {
          if (!settings.godMode) wave.stats.dmgTaken += eb.dmg;
          if (player.shield > 0) {
            player.shield = Math.max(0, player.shield - eb.dmg);
            player.invuln = 18;
          } else {
            if (!settings.godMode) player.hp -= eb.dmg;
            player.invuln = 36;
          }
          particles.burst(eb.x, eb.y, 6, eb.color, 220, 0.4);
          SFX.hit();
          ebullets.kill(eb);
          if (player.hp <= 0) onPlayerDeath();
          break; // one hit per frame — prevents multi-onPlayerDeath in same tick
        }
      }
    }

    // player ↔ enemy collision (with ramming)
    if (respawnT <= 0 && player.invuln <= 0) {
      const sp = Math.hypot(player.vx, player.vy);
      for (const e of enemies.enemies) {
        if (!e.alive) continue;
        const dx = player.x - e.x, dy = player.y - e.y;
        if (dx * dx + dy * dy < (e.radius + player.radius) * (e.radius + player.radius)) {
          // High-speed ramming damages enemy + deflects player
          if (sp > 400 && e.kind !== 'boss' && e.kind !== 'miniboss') {
            const ramDmg = sp / 8;
            if (e.eShield > 0) e.eShield = Math.max(0, e.eShield - ramDmg);
            else e.hp -= ramDmg;
            e.hitFlashT = 6;
            const d = Math.hypot(dx, dy) || 1;
            player.vx -= (dx / d) * sp * 0.4;
            player.vy -= (dy / d) * sp * 0.4;
            particles.burst(e.x, e.y, 12, 0xffaa00, 340, 0.5);
            SFX.boom();
            if (e.hp <= 0) {
              hud.score += Math.floor(e.val * 1.2);
              const drop = e.elite ? 'weapon' : rollPowerup(e.kind);
              if (drop) powerups.spawn(drop, e.x, e.y);
              enemies.kill(e, true);
              combo += 1; comboTimer = 2.6; hud.combo = combo; hud.comboPulse = 18;
              runStats.ramKills += 1;
              fireAch({ kind: 'ram_kill', n: runStats.ramKills });
            }
            player.invuln = 8;
            break;
          }
          const dmg = e.kind === 'asteroid' ? 8 : e.kind === 'tank' ? 18 : e.kind === 'boss' ? 25 : 10;
          if (player.shield > 0) {
            player.shield = Math.max(0, player.shield - dmg);
            player.invuln = 20;
          } else {
            if (!settings.godMode) player.hp -= dmg;
            player.invuln = 40;
          }
          particles.burst(player.x, player.y, 10, 0xff4444, 280, 0.5);
          SFX.hit();
          shake(8, 0.18);
          if (e.kind !== 'boss' && e.kind !== 'miniboss') {
            e.hp -= 999;
            enemies.kill(e, false);
          }
          if (player.hp <= 0) onPlayerDeath();
          break;
        }
      }
    }

    // powerup ↔ player
    if (respawnT <= 0) {
      for (let i = powerups.pool.length - 1; i >= 0; i--) {
        const p = powerups.pool[i];
        if (!p.alive) continue;
        const dx = p.x - player.x, dy = p.y - player.y;
        if (dx * dx + dy * dy < (p.r + player.radius) * (p.r + player.radius)) {
          if (p.type === 'health') {
            player.hp = Math.min(player.maxHp, player.hp + 20);
            floats.spawn('+20 COQUE', p.x, p.y, 0x00ff55);
          } else if (p.type === 'shield') {
            player.shield = Math.min(player.maxShield, player.shield + 12);
            floats.spawn('+12 BOUCLIER', p.x, p.y, 0x4488ff);
          }
          else if (p.type === 'weapon') {
            const tNow = G.frameCount / 60;
            if (p.gold) {
              const bumps = Math.min(3, WEAPONS.length - 1 - weaponIdx);
              weaponIdx = Math.min(WEAPONS.length - 1, weaponIdx + 3);
              hud.score += 600;
              particles.burst(p.x, p.y, 30, 0xffdd00, 360, 0.9, Math.PI * 2, 0, 2, 5);
              SFX.upgrade();
              wave.banner = { text: `★ DORÉ +${bumps} ★`, color: 0xffdd00, t: 100 };
              floats.spawn(`★ +${bumps} ★`, p.x, p.y, 0xffdd00, 22);
              fireAch({ kind: 'gold_pickup' });
            } else {
              if (tNow - lastWeaponPickupT < 1.5) {
                fx.synergy = 5;
                fireAch({ kind: 'synergy' });
              }
              if (weaponIdx < WEAPONS.length - 1) {
                weaponIdx += 1;
                floats.spawn(`NIV ${weaponIdx + 1}`, p.x, p.y, 0xff44ff);
              } else {
                hud.score += 600;
                floats.spawn('+600', p.x, p.y, 0xffaa00);
              }
            }
            lastWeaponPickupT = tNow;
          }
          else if (p.type === 'special') {
            specialCharges[0] = Math.min(3, specialCharges[0] + 1);
            floats.spawn('+SPÉCIAL', p.x, p.y, 0xffaa00);
          }
          else if (p.type === 'life') {
            lives += 1;
            floats.spawn('+1 VIE', p.x, p.y, 0xff2244);
          }
          // Per-pickup-kind SFX — each drop gets its own sonic identity
          if (p.type === 'health')      SFX.pickupHull();
          else if (p.type === 'shield') SFX.pickupShield();
          else if (p.type === 'special')SFX.pickupSpecial();
          else if (p.type === 'life')   SFX.pickupLife();
          else                          SFX.pickup();
          p.alive = false;
        }
      }
    }

    function onPlayerDeath() {
      shake(28, 0.7);
      // Phoenix Revive (F4) — once per run
      if (!runStats.phoenixUsed && phoenixLevel() >= 1) {
        runStats.phoenixUsed = true;
        player.hp = Math.ceil(player.maxHp * 0.5);
        player.invuln = 180;
        wave.banner = { text: 'RÉVEIL DU PHÉNIX', color: 0xffcc00, t: 130 };
        particles.burst(player.x, player.y, 30, 0xffcc00, 400, 1.0, Math.PI * 2, 0, 2, 6);
        SFX.special();
        return;
      }
      lives -= 1;
      SFX.death();
      // Explode in pieces — multi-layer burst with cyan ship debris + amber + red ember + white core
      particles.burst(player.x, player.y, 80, 0xffffff, 720, 0.40, Math.PI * 2, 0, 4.0, 9);
      particles.burst(player.x, player.y, 110, 0xffaa00, 600, 0.95, Math.PI * 2, 0, 3.0, 8);
      particles.burst(player.x, player.y, 70, 0x00ffff, 480, 1.10, Math.PI * 2, 0, 2.6, 7);
      particles.burst(player.x, player.y, 50, 0xff4422, 380, 1.45, Math.PI * 2, 0, 2.2, 6);
      triggerShockwave(player.x, player.y, 1.0, 32, 200);
      triggerFlash(0.65, 0xffaa44);
      if (lives < 0) {
        const earned = calcShardsEarned(hud.score, runStats.bossKills, runStats.wavesCleared);
        if (earned > 0) addShards(earned);
        if (qualifiesForLeaderboard(hud.score)) {
          const { idx } = addToLB(hud.score);
          lbHighlight.idx = idx;
          lbHighlight.t = 3;
        } else {
          lbHighlight.idx = -1;
        }
        G.state = 'gameover';
      } else {
        respawnT = 2.0;
      }
    }

    // Boss/miniboss phase-shift FX (banner, shockwave, flash, burst)
    tickBossPhaseShifts({ enemies: enemies.enemies, particles, triggerShockwave, triggerFlash, shake });

    // Boss arena modifiers
    if (wave.bossArena) {
      if (wave.bossArena === 'MINIONS') {
        wave.minionsT += dt;
        if (wave.minionsT >= 8) {
          wave.minionsT = 0;
          for (let i = 0; i < 2; i++) enemies.spawn('drone', wave.difficulty);
        }
      }
    }

    if (comboTimer > 0) {
      comboTimer -= dt;
      if (comboTimer <= 0) { combo = 0; hud.combo = 0; }
    }
    if (fx.synergy > 0) fx.synergy = Math.max(0, fx.synergy - dt);

    // Score multiplier zones (non-boss only) — gold rings, ×2 score while standing inside
    scoreMul = scoreZones.tick(dt, wave.wave % 5 !== 0 && !wave.waveBreak, player.x, player.y, G.W, G.H);

    const { newPops } = tickAchievements();
    achBanner.pushAll(newPops);

    tickWave(dt, enemies, weaponIdx, hud.score);
    tickBGM(dt, wave.wave, wave.wave % 5 === 0, false);
    // Continuous engine + friction layer + 10s mission-comms beacon
    engineAudio.tick(dt, player, { keys, respawnT });

    // Nova-bomb expanding ring — rapid nuclear shockwave telegraph
    if (novaRingT > 0) novaRingT = Math.max(0, novaRingT - dt);
    drawNovaRing(novaRing, novaRingX, novaRingY, novaRingR, novaRingT);

    // Charge ring around player — neon arc with outer halo
    drawChargeRing(chargeRing, player.x, player.y, charge.t, respawnT <= 0);

    // Boss phase telegraph (dashed aim line during warn window)
    drawBossTelegraph(telegraph, enemies.enemies, player.x, player.y);

    // DARKNESS arena dark mask — 4-rect spotlight cutout around player
    drawDarknessMask(darkness, wave.bossArena === 'DARKNESS' && wave.wave % 5 === 0, player.x, player.y, G.W, G.H);

    // Achievement banner (top-right) — glass card with amber accent strip
    achBanner.tick(dt, G.W);

    // Tutorial overlay
    if (tutStep > 0 && tutStep <= TUT_STEPS.length) {
      const step = TUT_STEPS[tutStep - 1];
      tutText.text = `DIDACTICIEL ${tutStep}/${TUT_STEPS.length} — ${step.msg}\n${step.hint}\n[T pour passer]`;
      tutText.x = G.W / 2;
      tutText.y = G.H - 100;
    } else {
      tutText.text = '';
    }
    // Score-gated auto-upgrade: +1 weapon level per 800 score, silent safety net
    const autoLevel = Math.min(Math.floor(hud.score / 800), WEAPONS.length - 1);
    if (autoLevel > lastAutoUpgradeWeaponIdx && weaponIdx < autoLevel) {
      weaponIdx = autoLevel;
      lastAutoUpgradeWeaponIdx = autoLevel;
      wave.banner = { text: `⚡ NIV ${weaponIdx + 1}`, color: 0xff88ff, t: 70 };
    }
    hud.weaponIdx = weaponIdx;
    player.setWeaponTier(weaponIdx);
    player.setWeaponName(WEAPONS[weaponIdx]?.name ?? '');
    hud.weaponName = WEAPONS[Math.min(weaponIdx, WEAPONS.length - 1)].name;
    hud.lives = lives;
    hud.endless = G.endless;
    hud.bombCharges = specialCharges[0];
    hud.synergyTimer = fx.synergy;
    hud.specialCharges = specialCharges;
    hud.specialIdx = specialIdx;
    hud.bashCD = bashCooldown;
    hud.missileTier = missileTier;
    hud.ringActive = ringActive;
    hud.bossInfo = null;
    for (const en of enemies.enemies) {
      if (!en.alive) continue;
      if (en.kind === 'boss' || en.kind === 'miniboss') {
        hud.bossInfo = { hp: en.hp, max: en.maxHp, phase: en.phase, kind: en.kind };
        break;
      }
    }
    hud.render(player, G.W, G.H);
  });

  console.log('[NOVA STRIKE] Pixi v8 boot OK', {
    renderer: app.renderer.type,
    width: app.renderer.width,
    height: app.renderer.height,
  });
}

boot().catch((err) => {
  console.error('[NOVA STRIKE] boot failed', err);
});
